import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import './TimeBlocking.css';
import '../styles/cards.css';
import { useAuth } from '../contexts/AuthContext';
import { useEffectiveWorkingHours } from '../shared/hooks/useAccountWorkingHours';
import {
  fetchUnscheduledTasks,
  fetchTimeBlocksForRange,
  fetchMissedTimeBlocks,
  fetchCompletedTimeBlocksByCalendarDate,
  fetchEarliestOpenTimeSlot,
  createTimeBlock,
  deleteTimeBlock,
  updateTimeBlock,
  closeTask,
  reopenTask,
  deleteTask,
  createTask,
  updateTask,
  fetchDeleteTaskImpact,
  formatDeleteTaskImpactSummary,
} from '../shared/api/tasks';
import {
  getGoogleCalendarAuthUrl,
  getGoogleCalendarStatus,
  disconnectGoogleCalendar,
  getGoogleCalendarEvents,
  syncFromGoogle,
  syncExistingBlocksToGoogle,
  GOOGLE_RECONNECT_REQUIRED,
} from '../shared/api/googleCalendar';
import { IconClose, IconReopen, IconBacklog, IconDelete, IconDocPlus, IconLightning, IconCalendar, IconChevronLeft, IconChevronRight, IconDuplicate, iconBtnStyle } from '../shared/icons/IconLib';
import { getRepeatLabel } from '../shared/utils/repeatOptions';
import RepeatPatternPicker from '../components/RepeatPatternPicker';
import { normalizeRepeatRule } from '../shared/utils/recurrence';
import { buildTimeOptionsForWorkingStart, getDueTimePart, getDueDatePart, getDueDateForApi, formatDueDate as formatDueDateFull } from '../shared/utils/dueDateTime';
import { buildDuplicateLabel } from '../shared/utils/duplicateLabel';
import { getItemLabels } from '../shared/utils/entityLabelFilter';
import { fieldCommitOnEnterKeyDown } from '../shared/utils/fieldCommitOnEnterKeyDown';
import TaskAddModal from '../components/TaskAddModal';
import SmartAddModal from '../components/SmartAddModal';
import ConfirmationModal from '../components/ConfirmationModal';
import TaskSectionTabs, { TASK_TAB_TIME_BLOCKING } from '../components/TaskSectionTabs';
import SectionTabsPageHeader from '../components/SectionTabsPageHeader';
import ProfileWorkingHoursPanel from '../components/ProfileWorkingHoursPanel';
import '../components/ProfileInfoModal.css';
import DuplicateSuccessMessage from '../components/DuplicateSuccessMessage';
import TimeSelect from '../components/TimeSelect';
import DateTimeFieldGroup from '../components/DateTimeFieldGroup';
import { taskHasTimeBlocks } from '../shared/utils/taskScheduleStatus';
import uiConfig from '../uiConfig';


import { apiFetch } from "../shared/api/apiFetch";

function formatDueDate(due) {
  if (!due) return '';
  const d = new Date(due);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Time Blocking calendar buckets: 30-minute slots
const CALENDAR_SLOT_MINUTES = 30;
const CALENDAR_SLOT_MS = CALENDAR_SLOT_MINUTES * 60 * 1000;
const SLOTS_PER_DAY = Math.floor((24 * 60) / CALENDAR_SLOT_MINUTES);

function formatSlotLabel(slotIndex) {
  const totalMinutes = slotIndex * CALENDAR_SLOT_MINUTES;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const d = new Date(2000, 0, 1, hours, minutes, 0);
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true });
}

function setDateToSlotStart(date, slotIndex) {
  const totalMinutes = slotIndex * CALENDAR_SLOT_MINUTES;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  date.setHours(hours, minutes, 0, 0);
}

function getSlotIndexFromDate(date) {
  return Math.floor((date.getHours() * 60 + date.getMinutes()) / CALENDAR_SLOT_MINUTES);
}

function getIsMobileBrowser() {
  if (typeof window === 'undefined') return false;
  const ua = String(window.navigator?.userAgent || '');
  const isUaMobile = /Mobi|Android|iPhone|iPad|iPod|Windows Phone/i.test(ua);
  const isSmall = typeof window.matchMedia === 'function' ? window.matchMedia('(max-width: 768px)').matches : false;
  const isCoarse = typeof window.matchMedia === 'function' ? window.matchMedia('(pointer: coarse)').matches : false;
  return Boolean(isUaMobile || isSmall || isCoarse);
}

/** Mongo project id on the task only (no Others fallback) — for /Task?projectId=… */
function getDetailProjectIdForLink(task) {
  if (!task) return '';
  if (task.source === 'block') {
    const pid = task.task_project_id;
    return pid != null && String(pid).trim() !== '' ? String(pid).trim() : '';
  }
  const pid = task.project_id;
  return pid != null && String(pid).trim() !== '' ? String(pid).trim() : '';
}

function getDetailProjectDisplayName(task) {
  if (!task) return '';
  if (task.source === 'block') {
    const n = task.task_project;
    return n != null && String(n).trim() !== '' ? String(n).trim() : '';
  }
  const n = task.project;
  return n != null && String(n).trim() !== '' ? String(n).trim() : '';
}

function TimeBlocking() {
  const [activeTab, setActiveTab] = useState('dailyPlanning');
  const [unscheduledTasks, setUnscheduledTasks] = useState([]);
  const [timeBlocks, setTimeBlocks] = useState([]);
  const [missedBlocks, setMissedBlocks] = useState([]);
  const [completedBlocks, setCompletedBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detailTask, setDetailTask] = useState(null);
  const [savingPriority, setSavingPriority] = useState(false);
  const [savingRepeat, setSavingRepeat] = useState(false);
  const [savingAmount, setSavingAmount] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [savingDueDate, setSavingDueDate] = useState(false);
  const [savingBlockTimes, setSavingBlockTimes] = useState(false);
  const [detailAmountDraft, setDetailAmountDraft] = useState('0');
  const [detailNameDraft, setDetailNameDraft] = useState('');
  const [detailDueDateDraft, setDetailDueDateDraft] = useState('');
  const [detailDueTimeDraft, setDetailDueTimeDraft] = useState('');
  const [detailRepeatDraft, setDetailRepeatDraft] = useState(null);
  const [detailPriorityDraft, setDetailPriorityDraft] = useState('');
  const [detailStartTimeDraft, setDetailStartTimeDraft] = useState('');
  const [detailEndTimeDraft, setDetailEndTimeDraft] = useState('');
  const [detailActiveField, setDetailActiveField] = useState('');
  const [detailInitialValues, setDetailInitialValues] = useState(null);
  const [savingDetail, setSavingDetail] = useState(false);
  const [duplicatingDetail, setDuplicatingDetail] = useState(false);
  const [completingBlockId, setCompletingBlockId] = useState(null);
  const [addTaskOpen, setAddTaskOpen] = useState(false);
  const [slotForNewTask, setSlotForNewTask] = useState(null); // { day: Date, slot: number } when creating from calendar slot
  const [smartAddOpen, setSmartAddOpen] = useState(false);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleConfigured, setGoogleConfigured] = useState(false);
  const [googleEvents, setGoogleEvents] = useState([]);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleSyncResult, setGoogleSyncResult] = useState(null);
  const [googleRefreshResult, setGoogleRefreshResult] = useState(null);
  const [googlePanelOpen, setGooglePanelOpen] = useState(false);
  const [workingHoursModalOpen, setWorkingHoursModalOpen] = useState(false);
  /** Shown when Google returns invalid_grant / stale OAuth (server clears connection). */
  const [googleAuthNotice, setGoogleAuthNotice] = useState('');
  const [autoSlotDialog, setAutoSlotDialog] = useState({ open: false, task: null, start: null, end: null });
  const [autoSlotAlert, setAutoSlotAlert] = useState({ open: false, title: '', message: '' });
  /** Unscheduled backlog: confirm delete with sub-task / time block counts */
  const [deleteUnscheduledTaskConfirm, setDeleteUnscheduledTaskConfirm] = useState({
    open: false,
    task: null,
    message: '',
  });
  /** When set, dismissing {@link autoSlotAlert} opens this task in the task details modal (e.g. after duplicate). */
  const taskDetailAfterAlertRef = useRef(null);
  const smartAddUnscheduledBeforeRef = useRef(new Set());
  /** After auto-schedule we set week/daily to the slot and fetch that range; skip duplicate load in the weekStart effect. */
  const skipNextWeekLoadRef = useRef(false);
  const { userInfo } = useAuth();
  const { workingHoursStart, workingHoursEnd, workingHoursStartHour } = useEffectiveWorkingHours();
  const taskAddDueTimeOptions = useMemo(
    () => buildTimeOptionsForWorkingStart(workingHoursStart),
    [workingHoursStart],
  );

  const [isMobileBrowser, setIsMobileBrowser] = useState(() => getIsMobileBrowser());

  useEffect(() => {
    const handle = () => setIsMobileBrowser(getIsMobileBrowser());
    window.addEventListener('resize', handle);
    window.addEventListener('orientationchange', handle);
    return () => {
      window.removeEventListener('resize', handle);
      window.removeEventListener('orientationchange', handle);
    };
  }, []);

  // Keep the Amount input synced with the currently opened task.
  useEffect(() => {
    if (!detailTask) return;
    const a = detailTask.amount != null ? Number(detailTask.amount) : 0;
    setDetailAmountDraft(String(Number.isFinite(a) ? a : 0));
  }, [detailTask]);

  useEffect(() => {
    if (!detailTask) return;
    const isBlock = detailTask.source === 'block';
    const name = isBlock ? (detailTask.task_name || '') : (detailTask.name || '');
    const due = isBlock ? detailTask.task_due_date : detailTask.due_date;
    setDetailNameDraft(String(name || ''));
    setDetailDueDateDraft(getDueDatePart(due) || '');
    setDetailDueTimeDraft(getDueTimePart(due) || '');
  }, [detailTask]);

  useEffect(() => {
    if (!detailTask) {
      setDetailInitialValues(null);
      setDetailActiveField('');
      setDetailStartTimeDraft('');
      setDetailEndTimeDraft('');
      return;
    }
    const isBlock = detailTask.source === 'block';
    const due = isBlock ? detailTask.task_due_date : detailTask.due_date;
    const amountNum = detailTask.amount != null ? Number(detailTask.amount) : 0;
    const repeatRule = normalizeRepeatRule(detailTask.repeat_rule ?? detailTask.repeat, getDueDatePart(due));
    const priority = (detailTask.priority != null && String(detailTask.priority).trim() !== '') ? String(detailTask.priority).trim() : '';
    const initial = {
      name: String(isBlock ? (detailTask.task_name || '') : (detailTask.name || '')),
      dueDate: getDueDatePart(due) || '',
      dueTime: getDueTimePart(due) || '',
      amount: String(Number.isFinite(amountNum) ? amountNum : 0),
      repeat_rule: repeatRule,
      priority,
    };
    setDetailInitialValues(initial);
    setDetailNameDraft(initial.name);
    setDetailDueDateDraft(initial.dueDate);
    setDetailDueTimeDraft(initial.dueTime);
    setDetailAmountDraft(initial.amount);
    setDetailRepeatDraft(initial.repeat_rule);
    setDetailPriorityDraft(initial.priority);

    if (isBlock) {
      const start = new Date(detailTask.start_time);
      const end = new Date(detailTask.end_time);
      setDetailStartTimeDraft(Number.isNaN(start.getTime()) ? '' : dayjs(start).format('HH:mm'));
      setDetailEndTimeDraft(Number.isNaN(end.getTime()) ? '' : dayjs(end).format('HH:mm'));
    } else {
      setDetailStartTimeDraft('');
      setDetailEndTimeDraft('');
    }
    setDetailActiveField('');
  }, [detailTask]);

  // Current week, Monday–Sunday (navigable)
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date();
    const day = d.getDay(); // 0=Sun
    const diff = (day === 0 ? -6 : 1) - day; // Monday as start
    d.setDate(d.getDate() + diff);
    d.setHours(0, 0, 0, 0);
    return d;
  });

  // Daily calendar base date (Today column) – navigable
  const [dailyBaseDate, setDailyBaseDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });

  const today = dailyBaseDate;
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const dailyDays = [today]; // Daily calendar shows base day only

  const accountId = userInfo?.account_id;
  const [othersProjectId, setOthersProjectId] = useState('');
  const userId = userInfo?._id || '';
  const role =
    Array.isArray(userInfo?.roles) && userInfo.roles.length > 0
      ? userInfo.roles[0]
      : '';

  const location = useLocation();
  const navigate = useNavigate();
  const focusSlotIso =
    location.state && typeof location.state.focusSlotStart === 'string'
      ? location.state.focusSlotStart
      : null;

  useEffect(() => {
    if (!accountId) return undefined;
    let cancelled = false;
    apiFetch(`${uiConfig.serverInfo.serverUrl}/getProject`, {
      method: 'POST',
      body: JSON.stringify({ account_id: accountId }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        const o = list.find((p) => (p.name || '').toLowerCase() === 'others');
        setOthersProjectId(o?._id != null ? String(o._id) : '');
      })
      .catch(() => {
        if (!cancelled) setOthersProjectId('');
      });
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  function getDetailProjectId(task) {
    if (!task) return '';
    if (task.source === 'block') {
      const pid = task.task_project_id;
      if (pid != null && String(pid).trim() !== '') return String(pid).trim();
      return othersProjectId || '';
    }
    const pid = task.project_id;
    if (pid != null && String(pid).trim() !== '') return String(pid).trim();
    return othersProjectId || '';
  }

  useEffect(() => {
    if (!accountId) return;

    if (skipNextWeekLoadRef.current) {
      skipNextWeekLoadRef.current = false;
      return undefined;
    }

    const weekEnd = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000);

    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const [tasks, blocks, missed, completed] = await Promise.all([
          fetchUnscheduledTasks(accountId, { user_id: userId, role }),
          fetchTimeBlocksForRange(accountId, weekStart.toISOString(), weekEnd.toISOString()),
          fetchMissedTimeBlocks(accountId),
          fetchCompletedTimeBlocksByCalendarDate(accountId, todayKey),
        ]);
        if (!cancelled) {
          setUnscheduledTasks(tasks);
          setTimeBlocks(blocks);
          setMissedBlocks(missed);
          setCompletedBlocks(completed);
        }
        if (userId && !cancelled) {
          try {
            const status = await getGoogleCalendarStatus(accountId, userId);
            setGoogleConnected(!!status.connected);
            setGoogleConfigured(!!status.configured);
            if (status.connected) {
              const events = await getGoogleCalendarEvents(
                accountId, userId, weekStart.toISOString(), weekEnd.toISOString()
              );
              if (!cancelled) setGoogleEvents(events || []);
              const syncResult = await syncFromGoogle(
                accountId, userId, weekStart.toISOString(), weekEnd.toISOString()
              );
              if (!cancelled && syncResult && ((syncResult.updated ?? 0) > 0 || (syncResult.movedToBacklog ?? 0) > 0)) {
                const [tasksAfter, blocksAfter, missedAfter, completedAfter] = await Promise.all([
                  fetchUnscheduledTasks(accountId, { user_id: userId, role }),
                  fetchTimeBlocksForRange(accountId, weekStart.toISOString(), weekEnd.toISOString()),
                  fetchMissedTimeBlocks(accountId),
                  fetchCompletedTimeBlocksByCalendarDate(accountId, todayKey),
                ]);
                setUnscheduledTasks(tasksAfter);
                setTimeBlocks(blocksAfter);
                setMissedBlocks(missedAfter);
                setCompletedBlocks(completedAfter);
              }
            } else {
              if (!cancelled) setGoogleEvents([]);
            }
          } catch (e) {
            if (!cancelled) {
              setGoogleEvents([]);
              if (e && e.code === GOOGLE_RECONNECT_REQUIRED) {
                setGoogleConnected(false);
                setGoogleAuthNotice(
                  e.message ||
                    'Google Calendar access expired or was revoked. Open Google Calendar below and connect again.'
                );
              }
            }
          }
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load time blocking data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [accountId, weekStart, userId, role, todayKey]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const google = params.get('google');
    if (google === 'connected') {
      setGoogleConnected(true);
      setGoogleConfigured(true);
      setGoogleAuthNotice('');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const [textSearch, setTextSearch] = useState('');
  const [unscheduledSort, setUnscheduledSort] = useState('due_asc'); // 'due_asc' | 'due_desc' | 'priority_high' | 'priority_low'

  const days = [...Array(7)].map((_, idx) => {
    const d = new Date(weekStart.getTime() + idx * 24 * 60 * 60 * 1000);
    return d;
  });

  const handlePrevWeek = () => {
    setWeekStart((prev) => {
      const d = new Date(prev.getTime());
      d.setDate(d.getDate() - 7);
      return d;
    });
  };

  const handleNextWeek = () => {
    setWeekStart((prev) => {
      const d = new Date(prev.getTime());
      d.setDate(d.getDate() + 7);
      return d;
    });
  };

  const handlePrevDay = () => {
    setDailyBaseDate((prev) => {
      const d = new Date(prev.getTime());
      d.setDate(d.getDate() - 1);
      return d;
    });
  };

  const handleNextDay = () => {
    setDailyBaseDate((prev) => {
      const d = new Date(prev.getTime());
      d.setDate(d.getDate() + 1);
      return d;
    });
  };

  /**
   * Daily calendar shows one day, but the app fetches time blocks by week range (weekStart..weekEnd).
   * If the user navigates the daily date into a different week, update weekStart so blocks reload.
   */
  useEffect(() => {
    if (activeTab !== 'dailyPlanning') return;
    if (!dailyBaseDate || Number.isNaN(dailyBaseDate.getTime())) return;
    if (!weekStart || Number.isNaN(weekStart.getTime())) return;

    const slotDay = new Date(dailyBaseDate.getTime());
    slotDay.setHours(0, 0, 0, 0);
    const monday = new Date(slotDay);
    const dow = monday.getDay();
    const diff = (dow === 0 ? -6 : 1) - dow; // Monday as start
    monday.setDate(monday.getDate() + diff);
    monday.setHours(0, 0, 0, 0);

    if (monday.toDateString() !== weekStart.toDateString()) {
      setWeekStart(monday);
    }
  }, [activeTab, dailyBaseDate, weekStart]);

  /** Must match server DEFAULT_MAX_DAYS (used for user-facing messages). */
  const AUTO_SCHEDULE_SEARCH_DAYS = 84;

  async function findEarliestOpenSlotAhead(maxDays = AUTO_SCHEDULE_SEARCH_DAYS) {
    if (!accountId) return null;
    const from_calendar_date = `${dailyBaseDate.getFullYear()}-${String(dailyBaseDate.getMonth() + 1).padStart(2, '0')}-${String(dailyBaseDate.getDate()).padStart(2, '0')}`;
    const viewer_time_zone =
      typeof Intl !== 'undefined' && Intl.DateTimeFormat
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : undefined;
    try {
      const slot = await fetchEarliestOpenTimeSlot({
        account_id: accountId,
        user_id: userId || undefined,
        from_calendar_date,
        max_days: maxDays,
        viewer_time_zone,
      });
      if (!slot) return null;
      return { start: new Date(slot.start_time), end: new Date(slot.end_time) };
    } catch (_) {
      return null;
    }
  }

  async function handleAutoSlotTask(task) {
    if (!task || !task._id) return;

    // Ensure we don't show the "Add task (scheduled in this slot)" modal during auto-schedule flows.
    setAddTaskOpen(false);
    setSlotForNewTask(null);

    const slot = await findEarliestOpenSlotAhead();
    if (!slot) {
      setAutoSlotAlert({
        open: true,
        title: 'No open slot found',
        message: `No free 30-minute slot in the next ${Math.round(AUTO_SCHEDULE_SEARCH_DAYS / 7)} weeks during your working hours (and synced calendar, if any).`,
      });
      return;
    }

    setAutoSlotDialog({ open: true, task, start: slot.start, end: slot.end });
  }

  /** Open from Task table: `/TimeBlocking?autoSchedule=<taskId>&tn=<encoded name>` */
  const autoScheduleFromUrlAppliedRef = useRef(false);
  useEffect(() => {
    if (loading) return;
    if (autoScheduleFromUrlAppliedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const taskId = params.get('autoSchedule');
    if (!taskId || !String(taskId).trim()) return;
    const tid = String(taskId).trim();
    const backlogTask = unscheduledTasks.find((t) => String(t._id) === tid);
    if (!backlogTask || taskHasTimeBlocks(backlogTask)) {
      autoScheduleFromUrlAppliedRef.current = true;
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    autoScheduleFromUrlAppliedRef.current = true;
    window.history.replaceState({}, '', window.location.pathname);
    void handleAutoSlotTask(backlogTask).catch(() => {
      setAutoSlotAlert({
        open: true,
        title: 'Auto schedule failed',
        message: 'Could not search for an open slot. Try again from Time Blocking.',
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run once when backlog list is ready; handleAutoSlotTask is stable for this URL flow
  }, [loading, unscheduledTasks]);

  // Show all 30-minute slots on the calendar; scrolling will determine which time range is visible.
  const slots = [...Array(SLOTS_PER_DAY)].map((_, idx) => idx); // 0–47

  // Block's start date in user's local time (calendar placement)
  const getBlockStartDateKey = (block) => {
    const d = new Date(block.start_time);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const scheduledToday = timeBlocks.filter((b) => {
    const dateKey = getBlockStartDateKey(b);
    const status = String(b.status || 'scheduled').toLowerCase();
    if (!(status === 'scheduled' && dateKey === todayKey)) return false;
    // If a scheduled block is already past its end_time, treat it as missed (do not show in scheduled list).
    const end = new Date(b.end_time);
    if (Number.isNaN(end.getTime())) return true;
    return end >= new Date();
  });

  const completedToday = completedBlocks;

  const missedAll = missedBlocks.filter((b) => {
    const status = String(b.status || 'scheduled').toLowerCase();
    if (status === 'missed') return true;
    if (status !== 'scheduled') return false;
    const end = new Date(b.end_time);
    if (Number.isNaN(end.getTime())) return false;
    const now = new Date();
    return end < now;
  });

  const searchTerm = (textSearch || '').toLowerCase().trim();

  const matchesTextSearch = useCallback((item) => {
    if (!searchTerm) return true;
    if (!item) return false;
    const fields = [
      // task fields
      item.name,
      item.project,
      item.category,
      item.notes,
      // time block fields (server typically denormalizes task metadata onto the block)
      item.task_name,
      item.task_project,
      item.task_category,
      item.task_notes,
    ];
    return fields.some((v) => (v != null ? String(v).toLowerCase() : '').includes(searchTerm));
  }, [searchTerm]);

  const filteredScheduledToday = useMemo(
    () => scheduledToday.filter((block) => matchesTextSearch(block)),
    [scheduledToday, matchesTextSearch]
  );

  const filteredMissedAll = useMemo(
    () => missedAll.filter((block) => matchesTextSearch(block)),
    [missedAll, matchesTextSearch]
  );

  const filteredCompletedToday = useMemo(
    () => completedToday.filter((block) => matchesTextSearch(block)),
    [completedToday, matchesTextSearch]
  );

  // Google event IDs we already show as time blocks — don't show them again as grey "Google" blocks
  const syncedGoogleEventIds = useMemo(
    () => new Set(timeBlocks.map((b) => b.google_event_id).filter(Boolean)),
    [timeBlocks]
  );

  async function refreshTasksAndBlocks() {
    if (!accountId) return;
    const weekEnd = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000);
    const [tasks, blocks, missed, completed] = await Promise.all([
      fetchUnscheduledTasks(accountId, { user_id: userId, role }),
      fetchTimeBlocksForRange(accountId, weekStart.toISOString(), weekEnd.toISOString()),
      fetchMissedTimeBlocks(accountId),
      fetchCompletedTimeBlocksByCalendarDate(accountId, todayKey),
    ]);
    setUnscheduledTasks(tasks);
    setTimeBlocks(blocks);
    setMissedBlocks(missed);
    setCompletedBlocks(completed);
    return { tasks, blocks, missed, completed };
  }

  /** Move weekly + daily views to the slot’s calendar day and reload blocks for that week (fixes auto-slot in a future week). */
  async function refreshTasksAndBlocksForSlotDay(slotStart) {
    if (!accountId || !slotStart) return null;
    const slotDay = new Date(slotStart.getTime());
    slotDay.setHours(0, 0, 0, 0);
    const slotDayKey = `${slotDay.getFullYear()}-${String(slotDay.getMonth() + 1).padStart(2, '0')}-${String(slotDay.getDate()).padStart(2, '0')}`;
    const monday = new Date(slotDay);
    const dow = monday.getDay();
    const diff = (dow === 0 ? -6 : 1) - dow;
    monday.setDate(monday.getDate() + diff);
    monday.setHours(0, 0, 0, 0);
    const rangeEnd = new Date(monday.getTime() + 7 * 24 * 60 * 60 * 1000);

    skipNextWeekLoadRef.current = true;
    setDailyBaseDate(slotDay);
    setWeekStart(monday);

    const [tasks, blocks, missed, completed] = await Promise.all([
      fetchUnscheduledTasks(accountId, { user_id: userId, role }),
      fetchTimeBlocksForRange(accountId, monday.toISOString(), rangeEnd.toISOString()),
      fetchMissedTimeBlocks(accountId),
      fetchCompletedTimeBlocksByCalendarDate(accountId, slotDayKey),
    ]);
    setUnscheduledTasks(tasks);
    setTimeBlocks(blocks);
    setMissedBlocks(missed);
    setCompletedBlocks(completed);

    if (userId && googleConnected) {
      try {
        const events = await getGoogleCalendarEvents(
          accountId,
          userId,
          monday.toISOString(),
          rangeEnd.toISOString(),
        );
        setGoogleEvents(Array.isArray(events) ? events : []);
      } catch (e) {
        setGoogleEvents([]);
        if (e && e.code === GOOGLE_RECONNECT_REQUIRED) {
          setGoogleConnected(false);
          setGoogleAuthNotice(
            e.message ||
              'Google Calendar access expired or was revoked. Open Google Calendar below and connect again.'
          );
        }
      }
    }
    return { tasks, blocks, missed, completed };
  }

  /** After scheduling from Task Management, align calendar to the new block (router state from navigate). */
  useEffect(() => {
    if (!accountId || !focusSlotIso) return;
    const d = new Date(focusSlotIso);
    if (Number.isNaN(d.getTime())) {
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }
    let cancelled = false;
    (async () => {
      await refreshTasksAndBlocksForSlotDay(d);
      if (!cancelled) {
        navigate(location.pathname, { replace: true, state: {} });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run when Task page passes focusSlotStart once
  }, [accountId, focusSlotIso, location.pathname, navigate]);

  async function handleCreateTaskFromModal(formValues) {
    if (!accountId) {
      throw new Error('Missing account.');
    }
    // Step 1: Create task (due_date from form, which defaults to calendar slot date when opened from slot)
    const created = await createTask({
      account_id: accountId,
      name: formValues.name,
      due_date: formValues.due_date,
      priority: formValues.priority || '',
      amount: formValues.amount,
      repeat_rule: formValues.repeat_rule,
      ...(othersProjectId && String(othersProjectId).trim() !== '' ? { project_id: othersProjectId } : {}),
      created_by: userId,
    });
    const taskId = created?._id;
    let slotStartForViewRefresh = null;

    // Step 2: If opened from calendar slot, schedule the new task at that slot time
    if (slotForNewTask && taskId) {
      const start = new Date(slotForNewTask.day.getTime());
      setDateToSlotStart(start, slotForNewTask.slot);
      const end = new Date(start.getTime() + CALENDAR_SLOT_MS);
      await createTimeBlock({
        account_id: accountId,
        task_id: taskId,
        user_id: userId,
        start_time: start.toISOString(),
        end_time: end.toISOString(),
      });
      slotStartForViewRefresh = start;
      setSlotForNewTask(null);
    } else if (formValues.autoSchedule && taskId) {
      const slotToUse = await findEarliestOpenSlotAhead();
      if (!slotToUse) {
        setAutoSlotAlert({
          open: true,
          title: 'No open slot found',
          message: `No free 30-minute slot in the next ${Math.round(AUTO_SCHEDULE_SEARCH_DAYS / 7)} weeks during your working hours (and synced calendar, if any).`,
        });
      } else {
        await createTimeBlock({
          account_id: accountId,
          task_id: taskId,
          user_id: userId,
          start_time: slotToUse.start.toISOString(),
          end_time: slotToUse.end.toISOString(),
          status: 'scheduled',
        });
        slotStartForViewRefresh = slotToUse.start;
      }
    }

    if (slotStartForViewRefresh) {
      await refreshTasksAndBlocksForSlotDay(slotStartForViewRefresh);
    } else {
      await refreshTasksAndBlocks();
    }
    setAddTaskOpen(false);
  }

  // Scroll calendar body so working hours start is visible by default (account setting, default 8 AM)
  const calendarBodyRef = useRef(null);

  const filteredUnscheduled = useMemo(
    () => unscheduledTasks.filter((task) => matchesTextSearch(task)),
    [unscheduledTasks, matchesTextSearch]
  );

  // Priority sort helper: High/P1, Medium/P2, Low/P3, then others
  const priorityOrder = (p) => {
    const s = (p != null ? String(p).trim() : '').toLowerCase();
    if (s === 'high' || s === 'p1') return 1;
    if (s === 'medium' || s === 'p2') return 2;
    if (s === 'low' || s === 'p3') return 3;
    return 4;
  };

  const sortedUnscheduled = useMemo(() => {
    const list = [...filteredUnscheduled];
    switch (unscheduledSort) {
      case 'due_asc':
        list.sort((a, b) => {
          const da = a.due_date ? new Date(a.due_date).getTime() : Infinity;
          const db = b.due_date ? new Date(b.due_date).getTime() : Infinity;
          return da - db;
        });
        break;
      case 'due_desc':
        list.sort((a, b) => {
          const da = a.due_date ? new Date(a.due_date).getTime() : 0;
          const db = b.due_date ? new Date(b.due_date).getTime() : 0;
          return db - da;
        });
        break;
      case 'priority_high':
        list.sort((a, b) => priorityOrder(a.priority) - priorityOrder(b.priority));
        break;
      case 'priority_low':
        list.sort((a, b) => priorityOrder(b.priority) - priorityOrder(a.priority));
        break;
      default:
        break;
    }
    return list;
  }, [filteredUnscheduled, unscheduledSort]);

  const renderUnscheduledPanel = () => (
    <div className="tb-unscheduled">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
        <h3 style={{ margin: 0 }}>Unscheduled Tasks</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            type="button"
            onClick={() => setUnscheduledSort(unscheduledSort === 'priority_high' ? 'due_asc' : 'priority_high')}
            title={unscheduledSort === 'priority_high' ? 'Sort: Priority (High to Low)' : 'Sort: Due Date (Ascending)'}
            aria-label="Toggle unscheduled sort"
            style={{
              border: '1px solid #d0d7de',
              background: '#f6f8fa',
              borderRadius: 4,
              padding: '4px 6px',
              cursor: 'pointer',
              fontSize: 12,
              lineHeight: 1,
            }}
          >
            ⇅
          </button>
          <button
            type="button"
            className="tb-task-complete-btn btn-link-muted"
            onClick={() => {
              setSlotForNewTask(null);
              setAddTaskOpen(true);
            }}
            title="Add Task"
            style={iconBtnStyle}
            aria-label="Add Task"
          >
            <IconDocPlus size={18} />
          </button>
          <button
            type="button"
            className="tb-task-complete-btn btn-link-muted"
            onClick={() => {
              smartAddUnscheduledBeforeRef.current = new Set(
                (unscheduledTasks || []).map((t) => String(t?._id))
              );
              setSmartAddOpen(true);
            }}
            title="Add Task (NLP)"
            style={iconBtnStyle}
            aria-label="Add Task (NLP)"
          >
            <IconLightning size={18} />
          </button>
        </div>
      </div>
      {loading && <div className="tb-help">Loading…</div>}
      {error && !loading && <div className="tb-error">{error}</div>}
      {!loading && !error && (
        <ul
          className="tb-task-list"
          onDragOver={(e) => { e.preventDefault(); }}
          onDrop={async (e) => {
            const type = e.dataTransfer.getData('type');
            const blockId = e.dataTransfer.getData('blockId');
            if (type !== 'block' || !blockId || !accountId) return;
            try {
              await deleteTimeBlock({ _id: blockId, account_id: accountId, user_id: userId });
              await refreshTasksAndBlocks();
            } catch (e2) {
              setError(e2?.message || 'Failed to remove time block');
            }
          }}
        >
          {sortedUnscheduled.length === 0 && (
            <li className="tb-task-empty">All open tasks are scheduled.</li>
          )}
          {sortedUnscheduled.map((task) => (
            <li
              key={task._id}
              className="tb-task-item tb-task-item--with-action"
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('type', 'task');
                e.dataTransfer.setData('taskId', task._id);
              }}
              onClick={() => setDetailTask({ source: 'task', ...task })}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setDetailTask({ source: 'task', ...task });
              }}
            >
              <div className="tb-task-item-content">
                <div className="tb-task-name" title={(task.name && String(task.name).trim()) || undefined}>
                  {task.name}
                </div>
                <div className="tb-task-meta">
                  {task.due_date && (
                    <span>Due {formatDueDate(task.due_date)}</span>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                {!taskHasTimeBlocks(task) && (
                <button
                  type="button"
                  className="tb-task-complete-btn btn-link-muted"
                  style={iconBtnStyle}
                  title="Auto schedule into earliest open 30-minute slot"
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleAutoSlotTask(task).catch(() => {
                      setAutoSlotAlert({
                        open: true,
                        title: 'Auto schedule failed',
                        message: 'Could not search for an open slot. Try again.',
                      });
                    });
                  }}
                  aria-label="Auto schedule"
                >
                  <IconBacklog size={18} style={{ transform: 'scaleX(-1)' }} />
                </button>
                )}
                <button
                  type="button"
                  className="tb-task-complete-btn btn-link-muted"
                  style={iconBtnStyle}
                  title="Delete task"
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleOpenDeleteUnscheduledConfirm(task);
                  }}
                  aria-label="Delete task"
                >
                  <IconDelete size={18} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const renderScheduledPanel = () => (
    <div className="tb-daily-right-column">
      <div className="tb-daily-list-column tb-daily-list-column--scheduled">
        <h3>Pending Tasks</h3>
        <ul className="tb-task-list">
          {filteredScheduledToday.length === 0 && (
            <li className="tb-task-empty">{searchTerm ? 'No matching pending tasks.' : 'No pending tasks for today.'}</li>
          )}
          {filteredScheduledToday.map((block) => {
            const start = new Date(block.start_time);
            const end = new Date(block.end_time);
            const startLabel = start.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
            const endLabel = end.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
            const isCompleting = completingBlockId === block._id;
            return (
              <li
                key={block._id}
                className="tb-task-item tb-task-item--with-action"
                role="button"
                tabIndex={0}
                onClick={() => setDetailTask({ source: 'block', ...block })}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setDetailTask({ source: 'block', ...block }); }}
              >
                <div className="tb-task-item-content">
                  <div className="tb-task-name" title={(block.task_name && String(block.task_name).trim()) || undefined}>
                    {block.task_name || 'Time block'}
                  </div>
                  <div className="tb-task-meta">
                    <span>{startLabel} – {endLabel}</span>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <button
                    type="button"
                    className="tb-task-complete-btn btn-link-muted"
                    style={iconBtnStyle}
                    title="Mark Complete"
                    disabled={isCompleting}
                    onClick={(e) => { e.stopPropagation(); handleMarkBlockCompleted(block); }}
                    aria-label="Close"
                  >
                    <IconClose size={18} />
                  </button>
                  <button
                    type="button"
                    className="tb-task-complete-btn btn-link-muted"
                    style={iconBtnStyle}
                    title="Move back to backlog"
                    onClick={(e) => { e.stopPropagation(); handleMoveMissedBlockToBacklog(block); }}
                    aria-label="Move back to backlog"
                  >
                    <IconBacklog size={18} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="tb-daily-list-column tb-daily-list-column--missed">
        <h3>Missed Tasks</h3>
        <ul className="tb-task-list">
          {filteredMissedAll.length === 0 && (
            <li className="tb-task-empty">{searchTerm ? 'No matching missed tasks.' : 'No missed tasks.'}</li>
          )}
          {filteredMissedAll.map((block) => {
            const start = new Date(block.start_time);
            const end = new Date(block.end_time);
            const startLabel = start.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
            const endLabel = end.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
            const isCompleting = completingBlockId === block._id;
            return (
              <li
                key={block._id}
                className="tb-task-item tb-task-item--with-action"
                role="button"
                tabIndex={0}
                onClick={() => setDetailTask({ source: 'block', ...block })}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setDetailTask({ source: 'block', ...block }); }}
              >
                <div className="tb-task-item-content">
                  <div className="tb-task-name" title={(block.task_name && String(block.task_name).trim()) || undefined}>
                    {block.task_name || 'Time block'}
                  </div>
                  <div className="tb-task-meta">
                    <span>{startLabel} – {endLabel}</span>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <button
                    type="button"
                    className="tb-task-complete-btn btn-link-muted"
                    style={iconBtnStyle}
                    title="Mark Complete"
                    disabled={isCompleting}
                    onClick={(e) => { e.stopPropagation(); handleMarkBlockCompleted(block); }}
                    aria-label="Close"
                  >
                    <IconClose size={18} />
                  </button>
                  <button
                    type="button"
                    className="tb-task-complete-btn btn-link-muted"
                    style={iconBtnStyle}
                    title="Move back to backlog"
                    onClick={(e) => { e.stopPropagation(); handleMoveMissedBlockToBacklog(block); }}
                    aria-label="Move back to backlog"
                  >
                    <IconBacklog size={18} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="tb-daily-list-column tb-daily-list-column--completed">
        <h3>Completed Tasks</h3>
        <ul className="tb-task-list">
          {filteredCompletedToday.length === 0 && (
            <li className="tb-task-empty">{searchTerm ? 'No matching completed tasks.' : 'No completed tasks for today.'}</li>
          )}
          {filteredCompletedToday.map((block) => {
            const start = new Date(block.start_time);
            const end = new Date(block.end_time);
            const startLabel = start.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
            const endLabel = end.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
            return (
              <li
                key={block._id}
                className="tb-task-item tb-task-item--with-action"
                role="button"
                tabIndex={0}
                onClick={() => setDetailTask({ source: 'block', ...block })}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setDetailTask({ source: 'block', ...block }); }}
              >
                <div className="tb-task-item-content">
                  <div className="tb-task-name" title={(block.task_name && String(block.task_name).trim()) || undefined}>
                    {block.task_name || 'Time block'}
                  </div>
                  <div className="tb-task-meta">
                    <span>{startLabel} – {endLabel}</span>
                  </div>
                </div>
                <button
                  type="button"
                  className="tb-task-complete-btn btn-link-muted"
                  style={iconBtnStyle}
                  title="Reopen & schedule again"
                  onClick={(e) => { e.stopPropagation(); handleReopenCompletedBlock(block); }}
                  aria-label="Reopen and schedule again"
                >
                  <IconReopen size={18} />
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );

  async function handleMarkBlockCompleted(block) {
    if (!accountId || !block?.task_id || !block?._id) return;
    setCompletingBlockId(block._id);
    setError('');
    try {
      await closeTask({
        _id: block.task_id,
        account_id: accountId,
        user_id: userId,
        viewer_time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      await updateTimeBlock({
        _id: block._id,
        account_id: accountId,
        user_id: userId,
        status: 'completed',
        calendar_date: todayKey,
      });
      await refreshTasksAndBlocks();
    } catch (e) {
      setError(e?.message || 'Failed to mark as completed');
    } finally {
      setCompletingBlockId(null);
    }
  }

  async function handleReopenCompletedBlock(block) {
    if (!accountId || !block?.task_id || !block?._id) return;
    setError('');
    try {
      await reopenTask({ _id: block.task_id, account_id: accountId });
      await refreshTasksAndBlocks();
    } catch (e) {
      setError(e?.message || 'Failed to reopen task');
    }
  }

  async function handleMoveMissedBlockToBacklog(block) {
    if (!accountId || !block?._id) return;
    setError('');
    try {
      await deleteTimeBlock({ _id: block._id, account_id: accountId, user_id: userId });
      await refreshTasksAndBlocks();
    } catch (e) {
      setError(e?.message || 'Failed to move back to backlog');
    }
  }

  async function handleDetailPriorityChange(newPriority) {
    if (!accountId || !detailTask) return;
    const isBlock = detailTask.source === 'block';
    const taskId = isBlock ? detailTask.task_id : detailTask._id;
    const name = String(detailNameDraft || (isBlock ? (detailTask.task_name || '') : (detailTask.name || ''))).trim();
    const project_id = getDetailProjectId(detailTask);
    const due = isBlock ? detailTask.task_due_date : detailTask.due_date;
    const dateStr = detailDueDateDraft || getDueDatePart(due) || '';
    const timeStr = getDueTimePart(due) || '00:00';
    const due_date = getDueDateForApi(dateStr, timeStr);
    if (!taskId || !name.trim() || !due_date) return;
    setSavingPriority(true);
    setError('');
    try {
      await updateTask({
        account_id: accountId,
        _id: taskId,
        name: name.trim(),
        due_date,
        priority: newPriority || '',
        ...(project_id ? { project_id } : {}),
      });
      setDetailTask((prev) => (prev ? { ...prev, priority: newPriority || '' } : null));
      setDetailInitialValues((prev) => (prev ? { ...prev, priority: newPriority || '' } : null));
      await refreshTasksAndBlocks();
    } catch (e) {
      setError(e?.message || 'Failed to update priority');
    } finally {
      setSavingPriority(false);
    }
  }

  async function handleDetailRepeatChange(newRepeatRule) {
    if (!accountId || !detailTask) return;
    const isBlock = detailTask.source === 'block';
    const taskId = isBlock ? detailTask.task_id : detailTask._id;
    const name = String(detailNameDraft || (isBlock ? (detailTask.task_name || '') : (detailTask.name || ''))).trim();
    const project_id = getDetailProjectId(detailTask);
    const due = isBlock ? detailTask.task_due_date : detailTask.due_date;
    const dateStr = detailDueDateDraft || getDueDatePart(due) || '';
    const timeStr = getDueTimePart(due) || '00:00';
    const due_date = getDueDateForApi(dateStr, timeStr);
    if (!taskId || !name.trim() || !due_date) return;

    const repeat_rule = normalizeRepeatRule(newRepeatRule, dateStr);

    setSavingRepeat(true);
    setError('');
    try {
      await updateTask({
        account_id: accountId,
        _id: taskId,
        name: name.trim(),
        due_date,
        repeat_rule,
        ...(project_id ? { project_id } : {}),
      });
      setDetailTask((prev) => (prev ? { ...prev, repeat_rule } : null));
      setDetailInitialValues((prev) => (prev ? { ...prev, repeat_rule } : null));
      await refreshTasksAndBlocks();
    } catch (e) {
      setError(e?.message || 'Failed to update repeat');
    } finally {
      setSavingRepeat(false);
    }
  }

  async function handleDetailAmountCommit(rawAmount) {
    if (!accountId || !detailTask) return;
    const isBlock = detailTask.source === 'block';
    const taskId = isBlock ? detailTask.task_id : detailTask._id;
    const name = String(detailNameDraft || (isBlock ? (detailTask.task_name || '') : (detailTask.name || ''))).trim();
    const project_id = getDetailProjectId(detailTask);
    const due = isBlock ? detailTask.task_due_date : detailTask.due_date;
    const dateStr = detailDueDateDraft || getDueDatePart(due) || '';
    const timeStr = getDueTimePart(due) || '00:00';
    const due_date = getDueDateForApi(dateStr, timeStr);
    if (!taskId || !name.trim() || !due_date) return;

    const amountNumRaw = Number(rawAmount);
    const amountNum = Number.isFinite(amountNumRaw) ? amountNumRaw : 0;

    setSavingAmount(true);
    setError('');
    try {
      await updateTask({
        account_id: accountId,
        _id: taskId,
        name: name.trim(),
        due_date,
        amount: amountNum,
        ...(project_id ? { project_id } : {}),
      });
      setDetailTask((prev) => (prev ? { ...prev, amount: amountNum } : null));
      setDetailInitialValues((prev) => (prev ? { ...prev, amount: String(amountNum) } : null));
      await refreshTasksAndBlocks();
    } catch (e) {
      setError(e?.message || 'Failed to update amount');
    } finally {
      setSavingAmount(false);
    }
  }

  async function handleDetailNameCommit(rawName) {
    if (!accountId || !detailTask) return;
    const isBlock = detailTask.source === 'block';
    const taskId = isBlock ? detailTask.task_id : detailTask._id;
    const project_id = getDetailProjectId(detailTask);
    const due = isBlock ? detailTask.task_due_date : detailTask.due_date;

    const nameTrim = String(rawName || '').trim();
    const dateStr = detailDueDateDraft || getDueDatePart(due) || '';
    const timeStr = getDueTimePart(due) || '00:00';
    const due_date = getDueDateForApi(dateStr, timeStr);

    if (!taskId || !nameTrim || !due_date) return;

    setSavingName(true);
    setError('');
    try {
      await updateTask({
        account_id: accountId,
        _id: taskId,
        name: nameTrim,
        due_date,
        ...(project_id ? { project_id } : {}),
      });

      setDetailTask((prev) => {
        if (!prev) return null;
        return isBlock
          ? { ...prev, task_name: nameTrim }
          : { ...prev, name: nameTrim };
      });
      setDetailInitialValues((prev) => (prev ? { ...prev, name: nameTrim } : null));

      await refreshTasksAndBlocks();
    } catch (e) {
      setError(e?.message || 'Failed to update task name');
    } finally {
      setSavingName(false);
    }
  }

  async function handleDetailDueDateCommit(rawDateStr, rawTimeStr) {
    if (!accountId || !detailTask) return;
    const isBlock = detailTask.source === 'block';
    const taskId = isBlock ? detailTask.task_id : detailTask._id;
    const project_id = getDetailProjectId(detailTask);
    const due = isBlock ? detailTask.task_due_date : detailTask.due_date;

    const dateStr = String(rawDateStr || '').trim();
    const timeFromArg = rawTimeStr != null ? String(rawTimeStr).trim() : '';
    const timeStr =
      rawTimeStr !== undefined
        ? (timeFromArg || '00:00')
        : (String(detailDueTimeDraft || '').trim() || getDueTimePart(due) || '00:00');
    const due_date = getDueDateForApi(dateStr, timeStr);

    const nameTrim = String(detailNameDraft || '').trim();

    if (!taskId || !nameTrim || !due_date) return;

    setSavingDueDate(true);
    setError('');
    try {
      await updateTask({
        account_id: accountId,
        _id: taskId,
        name: nameTrim,
        due_date,
        ...(project_id ? { project_id } : {}),
      });

      setDetailTask((prev) => {
        if (!prev) return null;
        return isBlock
          ? { ...prev, task_due_date: due_date }
          : { ...prev, due_date };
      });
      const savedTime = getDueTimePart(due_date) || '';
      setDetailDueTimeDraft(savedTime);
      setDetailInitialValues((prev) =>
        prev ? { ...prev, dueDate: dateStr, dueTime: savedTime } : null
      );

      await refreshTasksAndBlocks();
    } catch (e) {
      setError(e?.message || 'Failed to update due date');
    } finally {
      setSavingDueDate(false);
    }
  }

  function commitDetailDueFieldBlur() {
    if (!detailInitialValues) {
      setDetailActiveField('');
      return;
    }
    const d = String(detailDueDateDraft || '').trim();
    const t = String(detailDueTimeDraft || '').trim();
    const sameDate = d === String(detailInitialValues.dueDate || '').trim();
    const sameTime = t === String(detailInitialValues.dueTime || '').trim();
    if (sameDate && sameTime) {
      setDetailActiveField('');
      return;
    }
    void handleDetailDueDateCommit(detailDueDateDraft, detailDueTimeDraft).then(() =>
      setDetailActiveField('')
    );
  }

  async function handleDetailBlockStartCommit(rawTimeStr) {
    if (!accountId || !detailTask) return false;
    if (detailTask.source !== 'block') return false;
    const blockId = detailTask._id;
    if (!blockId) return false;

    const timeStr = String(rawTimeStr || '').trim();
    if (!/^\d{2}:\d{2}$/.test(timeStr)) {
      setError('Invalid start time.');
      return false;
    }
    const [hhStr, mmStr] = timeStr.split(':');
    const hh = parseInt(hhStr, 10);
    const mm = parseInt(mmStr, 10);
    if (!Number.isFinite(hh) || !Number.isFinite(mm) || hh < 0 || hh > 23 || mm < 0 || mm > 59) {
      setError('Invalid start time.');
      return false;
    }

    const currentBlockStatus = String(detailTask.status || 'scheduled').toLowerCase();

    const existingStart = new Date(detailTask.start_time);
    const existingEnd = new Date(detailTask.end_time);
    if (Number.isNaN(existingStart.getTime()) || Number.isNaN(existingEnd.getTime())) {
      setError('Invalid block times.');
      return false;
    }

    const newStart = new Date(existingStart.getFullYear(), existingStart.getMonth(), existingStart.getDate(), hh, mm, 0, 0);
    let newEnd;
    if (/^\d{2}:\d{2}$/.test(String(detailEndTimeDraft || '').trim())) {
      const [endHhStr, endMmStr] = String(detailEndTimeDraft).trim().split(':');
      const endHh = parseInt(endHhStr, 10);
      const endMm = parseInt(endMmStr, 10);
      newEnd = new Date(newStart.getFullYear(), newStart.getMonth(), newStart.getDate(), endHh, endMm, 0, 0);
    } else {
      newEnd = new Date(existingEnd.getTime());
    }

    // If start is moved to or beyond end, snap end to next 30-minute slot.
    if (newEnd <= newStart) {
      newEnd = new Date(newStart.getTime() + CALENDAR_SLOT_MS);
    }

    if (Number.isNaN(newEnd.getTime()) || newEnd <= newStart) {
      setError('Invalid time range.');
      return false;
    }
    // Prevent crossing into the next day (dropdown only represents same-day times).
    if (
      newStart.getFullYear() !== newEnd.getFullYear()
      || newStart.getMonth() !== newEnd.getMonth()
      || newStart.getDate() !== newEnd.getDate()
    ) {
      setError('Start time is too late for this block duration (would end after midnight).');
      return false;
    }

    const nextStatus =
      currentBlockStatus === 'completed'
        ? 'completed'
        : (newEnd.getTime() < Date.now() ? 'missed' : 'scheduled');

    setSavingBlockTimes(true);
    setError('');
    try {
      await updateTimeBlock({
        _id: blockId,
        account_id: accountId,
        user_id: userId,
        start_time: newStart.toISOString(),
        end_time: newEnd.toISOString(),
        status: nextStatus,
      });

      const durationMinutesNext = Math.round((newEnd.getTime() - newStart.getTime()) / 60000);
      setDetailTask((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          start_time: newStart.toISOString(),
          end_time: newEnd.toISOString(),
          duration_minutes: durationMinutesNext,
          status: nextStatus,
        };
      });

      setDetailStartTimeDraft(timeStr);
      setDetailEndTimeDraft(dayjs(newEnd).format('HH:mm'));
      setDetailActiveField('');
      await refreshTasksAndBlocks();
      return true;
    } catch (e) {
      setError(e?.message || 'Failed to update block start time');
      return false;
    } finally {
      setSavingBlockTimes(false);
    }
  }

  async function handleDetailBlockEndCommit(rawTimeStr) {
    if (!accountId || !detailTask) return false;
    if (detailTask.source !== 'block') return false;
    const blockId = detailTask._id;
    if (!blockId) return false;

    const timeStr = String(rawTimeStr || '').trim();
    if (!/^\d{2}:\d{2}$/.test(timeStr)) {
      setError('Invalid end time.');
      return false;
    }
    const [hhStr, mmStr] = timeStr.split(':');
    const hh = parseInt(hhStr, 10);
    const mm = parseInt(mmStr, 10);
    if (!Number.isFinite(hh) || !Number.isFinite(mm) || hh < 0 || hh > 23 || mm < 0 || mm > 59) {
      setError('Invalid end time.');
      return false;
    }

    const currentBlockStatus = String(detailTask.status || 'scheduled').toLowerCase();

    const existingStart = new Date(detailTask.start_time);
    const existingEnd = new Date(detailTask.end_time);
    if (Number.isNaN(existingStart.getTime()) || Number.isNaN(existingEnd.getTime())) {
      setError('Invalid block times.');
      return false;
    }

    const newEnd = new Date(existingEnd.getFullYear(), existingEnd.getMonth(), existingEnd.getDate(), hh, mm, 0, 0);
    let newStart = new Date(existingStart.getTime());
    if (newEnd <= newStart) {
      newStart = new Date(newEnd.getTime() - CALENDAR_SLOT_MS);
    }
    if (
      newStart.getFullYear() !== newEnd.getFullYear()
      || newStart.getMonth() !== newEnd.getMonth()
      || newStart.getDate() !== newEnd.getDate()
    ) {
      setError('End time is too early for this block duration (would start previous day).');
      return false;
    }

    const nextStatus =
      currentBlockStatus === 'completed'
        ? 'completed'
        : (newEnd.getTime() < Date.now() ? 'missed' : 'scheduled');

    setSavingBlockTimes(true);
    setError('');
    try {
      await updateTimeBlock({
        _id: blockId,
        account_id: accountId,
        user_id: userId,
        start_time: newStart.toISOString(),
        end_time: newEnd.toISOString(),
        status: nextStatus,
      });

      const durationMinutesNext = Math.round((newEnd.getTime() - newStart.getTime()) / 60000);
      setDetailTask((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          start_time: newStart.toISOString(),
          end_time: newEnd.toISOString(),
          duration_minutes: durationMinutesNext,
          status: nextStatus,
        };
      });

      setDetailStartTimeDraft(dayjs(newStart).format('HH:mm'));
      setDetailEndTimeDraft(timeStr);
      setDetailActiveField('');
      await refreshTasksAndBlocks();
      return true;
    } catch (e) {
      setError(e?.message || 'Failed to update block end time');
      return false;
    } finally {
      setSavingBlockTimes(false);
    }
  }

  /** @returns {Promise<boolean>} */
  async function persistAllDetailFields() {
    if (!accountId || !detailTask) return true;
    const isBlock = detailTask.source === 'block';
    const taskId = isBlock ? detailTask.task_id : detailTask._id;
    const project_id = getDetailProjectId(detailTask);
    const due = isBlock ? detailTask.task_due_date : detailTask.due_date;
    const timeStr = String(detailDueTimeDraft || '').trim() || getDueTimePart(due) || '00:00';
    const dateStr = String(detailDueDateDraft || '').trim();
    const due_date = getDueDateForApi(dateStr, timeStr);
    const nameTrim = String(detailNameDraft || '').trim();
    const amountNumRaw = Number(detailAmountDraft);
    const amountNum = Number.isFinite(amountNumRaw) ? amountNumRaw : 0;
    const priority = String(detailPriorityDraft || '').trim();

    // `project_id` is required for some operations, but it shouldn't block
    // normal edits (name/due/amount/etc.) since the server can keep the
    // existing `project_id` when omitted.
    if (!taskId || !nameTrim || !due_date) {
      if (!nameTrim) setError('Task name is required before closing.');
      else if (!due_date) setError('Due date is required before closing.');
      return false;
    }

    setSavingDetail(true);
    setError('');
    try {
      await updateTask({
        account_id: accountId,
        _id: taskId,
        name: nameTrim,
        due_date,
        amount: amountNum,
        repeat_rule: normalizeRepeatRule(detailRepeatDraft, detailDueDateDraft),
        priority,
        ...(project_id ? { project_id } : {}),
      });

      setDetailTask((prev) => {
        if (!prev) return null;
        return isBlock
          ? { ...prev, task_name: nameTrim, task_due_date: due_date, amount: amountNum, repeat_rule: normalizeRepeatRule(detailRepeatDraft, detailDueDateDraft), priority }
          : { ...prev, name: nameTrim, due_date, amount: amountNum, repeat_rule: normalizeRepeatRule(detailRepeatDraft, detailDueDateDraft), priority };
      });

      const nextInitial = {
        name: nameTrim,
        dueDate: dateStr,
        dueTime: getDueTimePart(due_date) || '',
        amount: String(amountNum),
        repeat_rule: normalizeRepeatRule(detailRepeatDraft, detailDueDateDraft),
        priority,
      };
      setDetailInitialValues(nextInitial);
      setDetailNameDraft(nextInitial.name);
      setDetailDueDateDraft(nextInitial.dueDate);
      setDetailDueTimeDraft(nextInitial.dueTime);
      setDetailAmountDraft(nextInitial.amount);
      setDetailRepeatDraft(nextInitial.repeat_rule);
      setDetailPriorityDraft(nextInitial.priority);
      setDetailActiveField('');
      await refreshTasksAndBlocks();
      return true;
    } catch (e) {
      setError(e?.message || 'Failed to update task details');
      return false;
    } finally {
      setSavingDetail(false);
    }
  }

  function detailModalHasUnsavedChanges() {
    if (!detailInitialValues) return false;
    return (
      String(detailNameDraft || '') !== String(detailInitialValues.name || '')
      || String(detailDueDateDraft || '') !== String(detailInitialValues.dueDate || '')
      || String(detailDueTimeDraft || '') !== String(detailInitialValues.dueTime || '')
      || String(detailAmountDraft || '') !== String(detailInitialValues.amount || '')
      || JSON.stringify(detailRepeatDraft) !== JSON.stringify(detailInitialValues.repeat_rule)
      || String(detailPriorityDraft || '') !== String(detailInitialValues.priority || '')
    );
  }

  async function closeDetailTaskModal() {
    if (detailTask && detailModalHasUnsavedChanges()) {
      const ok = await persistAllDetailFields();
      if (!ok) return;
    }
    setDetailTask(null);
  }

  function dismissAutoSlotAlert() {
    const nextDetail = taskDetailAfterAlertRef.current;
    taskDetailAfterAlertRef.current = null;
    setAutoSlotAlert({ open: false, title: '', message: '' });
    if (nextDetail) {
      setDetailTask(nextDetail);
    }
  }

  async function handleDuplicateDetailTask() {
    if (!accountId || !detailTask || duplicatingDetail) return;
    const isBlock = detailTask.source === 'block';
    const due = isBlock ? detailTask.task_due_date : detailTask.due_date;
    const timeStr = String(detailDueTimeDraft || '').trim() || getDueTimePart(due) || '00:00';
    const dateStr = String(detailDueDateDraft || '').trim() || getDueDatePart(due) || '';
    const due_date = getDueDateForApi(dateStr, timeStr);
    const nameTrim = String(detailNameDraft || '').trim();
    const baseName = nameTrim || (isBlock ? (detailTask.task_name || '') : (detailTask.name || '')) || 'Task';
    const duplicateName = buildDuplicateLabel(baseName, { fallback: 'Task' });
    const project_id = getDetailProjectId(detailTask);
    const displayProject = (isBlock ? detailTask.task_project : detailTask.project) || 'Others';
    const amountNumRaw = Number(detailAmountDraft);
    const amountNum = Number.isFinite(amountNumRaw) ? amountNumRaw : 0;
    const priority = String(detailPriorityDraft || '').trim();

    if (!due_date) {
      setError('Due date is required to duplicate this task.');
      return;
    }

    setDuplicatingDetail(true);
    setError('');
    try {
      const sourceForCopy = isBlock
        ? (unscheduledTasks.find((t) => t != null && String(t._id) === String(detailTask.task_id)) || detailTask)
        : detailTask;
      const sourceLabels = getItemLabels(sourceForCopy);
      const sourceSeq = sourceForCopy.seq;
      const createPayload = {
        account_id: accountId,
        name: duplicateName,
        due_date,
        amount: amountNum,
        repeat_rule: normalizeRepeatRule(detailRepeatDraft, detailDueDateDraft),
        project_id,
        priority,
        created_by: userId,
        labels: sourceLabels,
      };
      if (sourceSeq != null && sourceSeq !== '') {
        const seqNum = Number(sourceSeq);
        if (Number.isInteger(seqNum) && seqNum >= 0) createPayload.seq = seqNum;
      }
      const created = await createTask(createPayload);
      const refreshed = await refreshTasksAndBlocks();
      const newId = created?._id != null ? String(created._id) : '';
      let nextDetail = null;
      if (newId && refreshed?.tasks) {
        const t = refreshed.tasks.find((x) => x != null && String(x._id) === newId);
        if (t) nextDetail = { source: 'task', ...t };
      }
      if (!nextDetail && newId) {
        nextDetail = {
          source: 'task',
          _id: newId,
          name: duplicateName,
          due_date,
          amount: amountNum,
          repeat_rule: normalizeRepeatRule(detailRepeatDraft, detailDueDateDraft),
          priority,
          project_id,
          project: displayProject,
          status: 'Open',
        };
      }
      taskDetailAfterAlertRef.current = nextDetail;
      const openDuplicatedFromAlert = () => {
        const next = taskDetailAfterAlertRef.current;
        taskDetailAfterAlertRef.current = null;
        setAutoSlotAlert({ open: false, title: '', message: '' });
        if (next) setDetailTask(next);
      };
      setAutoSlotAlert({
        open: true,
        title: 'Task duplicated',
        message:
          newId ? (
            <>
              <DuplicateSuccessMessage
                linkLabel={duplicateName.trim() || 'Duplicated task'}
                onOpenDetail={openDuplicatedFromAlert}
                footer={null}
              />
              <div style={{ marginTop: '12px', fontSize: '13px', color: '#6c757d' }}>
                You can also press OK to open the new task for editing.
              </div>
            </>
          ) : (
            `Created "${duplicateName.trim() || 'Duplicated task'}".\n\n`
          + 'When you close this message, the new task opens here so you can edit it.'
          ),
      });
    } catch (e) {
      setError(e?.message || 'Failed to duplicate task');
    } finally {
      setDuplicatingDetail(false);
    }
  }

  async function handleOpenDeleteUnscheduledConfirm(task) {
    if (!accountId || !task?._id) return;
    setError('');
    let impactSummary = formatDeleteTaskImpactSummary(null);
    try {
      const impact = await fetchDeleteTaskImpact({ account_id: accountId, _id: task._id });
      impactSummary = formatDeleteTaskImpactSummary(impact);
    } catch {
      impactSummary = formatDeleteTaskImpactSummary(null);
    }
    const rawName = (task.name || 'this task').trim() || 'this task';
    const displayLabel = rawName.length > 80 ? `${rawName.slice(0, 80)}…` : rawName;
    setDeleteUnscheduledTaskConfirm({
      open: true,
      task,
      message: `Delete "${displayLabel}"?\n\n${impactSummary}\n\nThis cannot be undone.`,
    });
  }

  async function executeDeleteUnscheduledTask() {
    const task = deleteUnscheduledTaskConfirm.task;
    setDeleteUnscheduledTaskConfirm({ open: false, task: null, message: '' });
    if (!accountId || !task?._id) return;
    setError('');
    try {
      await deleteTask({ _id: task._id, account_id: accountId });
      await refreshTasksAndBlocks();
    } catch (e) {
      setError(e?.message || 'Failed to delete task');
    }
  }

  // Note: moving individual missed blocks back to backlog is handled per-card via handleMoveMissedBlockToBacklog.

  async function handleDropOnCell(e, targetDate, slot) {
    e.preventDefault();
    if (!accountId) return;

    const type = e.dataTransfer.getData('type');

    if (type === 'task') {
      const taskId = e.dataTransfer.getData('taskId');
      if (!taskId) return;

      // Prevent multiple tasks in the same time bucket (day + slot)
      const cellStart = new Date(targetDate.getTime());
      setDateToSlotStart(cellStart, slot);
      const cellEnd = new Date(cellStart.getTime() + CALENDAR_SLOT_MS);
      const hasExistingInBucket = timeBlocks.some((b) => {
        const start = new Date(b.start_time);
        const end = new Date(b.end_time);
        return (
          start.getFullYear() === cellStart.getFullYear()
          && start.getMonth() === cellStart.getMonth()
          && start.getDate() === cellStart.getDate()
          && start < cellEnd
          && end > cellStart
        );
      });
      if (hasExistingInBucket) {
        return;
      }

      const start = new Date(targetDate.getTime());
      setDateToSlotStart(start, slot);
      const end = new Date(start.getTime() + CALENDAR_SLOT_MS);

      try {
        await createTimeBlock({
          account_id: accountId,
          task_id: taskId,
          user_id: userId,
          start_time: start.toISOString(),
          end_time: end.toISOString(),
        });
        await refreshTasksAndBlocks();
      } catch (e) {
        setError(e?.message || 'Failed to create time block');
      }
      return;
    }

    if (type === 'block') {
      const blockId = e.dataTransfer.getData('blockId');
      if (!blockId) return;

      const existing = timeBlocks.find((b) => String(b._id) === String(blockId));
      if (!existing) return;

      const durationMinutes = Number(existing.duration_minutes || 60);

      const newStart = new Date(targetDate.getTime());
      setDateToSlotStart(newStart, slot);
      const newEnd = new Date(newStart.getTime() + durationMinutes * 60 * 1000);

      try {
        await updateTimeBlock({
          _id: blockId,
          account_id: accountId,
          user_id: userId,
          start_time: newStart.toISOString(),
          end_time: newEnd.toISOString(),
          status: 'scheduled',
        });
        await refreshTasksAndBlocks();
      } catch (e) {
        setError(e?.message || 'Failed to move time block');
      }
    }
    if (type === 'block-resize') {
      const blockId = e.dataTransfer.getData('blockId');
      const startDateKey = e.dataTransfer.getData('startDate');
      const startSlotStr = e.dataTransfer.getData('startSlot');
      if (!blockId || !startDateKey || !startSlotStr) return;

      const startSlot = Number(startSlotStr);
      if (Number.isNaN(startSlot)) return;

      const targetDateKey = targetDate.toISOString().slice(0, 10);
      if (targetDateKey !== startDateKey || slot < startSlot) {
        return;
      }

      const existing = timeBlocks.find((b) => String(b._id) === String(blockId));
      if (!existing) return;

      const originalStart = new Date(existing.start_time);
      const slotSpan = slot - startSlot + 1;
      const newEnd = new Date(originalStart.getTime() + slotSpan * CALENDAR_SLOT_MS);

      try {
        await updateTimeBlock({
          _id: blockId,
          account_id: accountId,
          user_id: userId,
          end_time: newEnd.toISOString(),
          status: 'scheduled',
        });
        await refreshTasksAndBlocks();
      } catch (e) {
        setError(e?.message || 'Failed to resize time block');
      }
    }
  }

  async function handleGoogleConnect() {
    if (!accountId || !userId) return;
    setGoogleAuthNotice('');
    setGoogleLoading(true);
    try {
      const { url } = await getGoogleCalendarAuthUrl(accountId, userId);
      if (url) window.location.href = url;
    } catch (e) {
      setError(e?.message || 'Failed to get Google Calendar link');
    } finally {
      setGoogleLoading(false);
    }
  }

  async function handleGoogleDisconnect() {
    if (!accountId || !userId) return;
    setGoogleLoading(true);
    setGoogleSyncResult(null);
    setGoogleAuthNotice('');
    try {
      await disconnectGoogleCalendar(accountId, userId);
      setGoogleConnected(false);
      setGoogleEvents([]);
    } catch (e) {
      setError(e?.message || 'Failed to disconnect');
    } finally {
      setGoogleLoading(false);
    }
  }

  async function handleSyncExistingToGoogle() {
    if (!accountId || !userId) return;
    const weekEnd = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000);
    setGoogleLoading(true);
    setGoogleSyncResult(null);
    setGoogleRefreshResult(null);
    setError('');
    try {
      const { synced } = await syncExistingBlocksToGoogle(
        accountId, userId, weekStart.toISOString(), weekEnd.toISOString()
      );
      setGoogleSyncResult(synced);
      await refreshTasksAndBlocks();
    } catch (e) {
      setError(e?.message || 'Failed to sync to Google');
    } finally {
      setGoogleLoading(false);
    }
  }

  async function handleRefreshFromGoogle() {
    if (!accountId || !userId) return;
    const weekEnd = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000);
    const startIso = weekStart.toISOString();
    const endIso = weekEnd.toISOString();
    setGoogleLoading(true);
    setGoogleRefreshResult(null);
    setError('');
    try {
      const [syncResult, events] = await Promise.all([
        syncFromGoogle(accountId, userId, startIso, endIso),
        getGoogleCalendarEvents(accountId, userId, startIso, endIso),
      ]);
      setGoogleRefreshResult(syncResult || { updated: 0, movedToBacklog: 0 });
      setGoogleEvents(events || []);
      if (syncResult && (syncResult.updated > 0 || (syncResult.movedToBacklog ?? 0) > 0)) {
        await refreshTasksAndBlocks();
      }
    } catch (e) {
      if (e && e.code === GOOGLE_RECONNECT_REQUIRED) {
        setGoogleConnected(false);
        setGoogleAuthNotice(
          e.message ||
            'Google Calendar access expired or was revoked. Connect again below.'
        );
        setGoogleEvents([]);
      } else {
        setError(e?.message || 'Failed to refresh from Google');
      }
    } finally {
      setGoogleLoading(false);
    }
  }

  const [mobileSection, setMobileSection] = useState('calendar'); // 'unscheduled' | 'calendar' | 'scheduled'
  const [webSections, setWebSections] = useState(() => ({
    unscheduled: true,
    calendar: true,
    scheduled: true,
  }));

  const taskViewDropdownRef = useRef(null);
  const [taskViewDropdownOpen, setTaskViewDropdownOpen] = useState(false);

  useEffect(() => {
    if (isMobileBrowser) return;
    if (activeTab === 'weeklyPlanning') {
      setWebSections({ unscheduled: true, calendar: true, scheduled: false });
      return;
    }
    if (activeTab === 'dailyPlanning') {
      setWebSections({ unscheduled: true, calendar: true, scheduled: true });
    }
  }, [activeTab, isMobileBrowser]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (taskViewDropdownRef.current && !taskViewDropdownRef.current.contains(e.target)) {
        setTaskViewDropdownOpen(false);
      }
    }
    if (taskViewDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [taskViewDropdownOpen]);

  const selectedSections = useMemo(() => {
    if (isMobileBrowser) {
      return {
        unscheduled: mobileSection === 'unscheduled',
        calendar: mobileSection === 'calendar',
        scheduled: mobileSection === 'scheduled',
      };
    }
    return webSections;
  }, [isMobileBrowser, mobileSection, webSections]);

  const showUnscheduledPanel = !!selectedSections.unscheduled;
  const showCalendar = !!selectedSections.calendar;
  const showScheduledPanel = !!selectedSections.scheduled;

  useEffect(() => {
    if (!showCalendar || !calendarBodyRef.current) return;
    const style = typeof window !== 'undefined' ? window.getComputedStyle(calendarBodyRef.current) : null;
    const rowHeightPx = style ? Number.parseFloat(style.getPropertyValue('--tb-hour-row-height')) : NaN;
    const rowHeight = Number.isFinite(rowHeightPx) ? rowHeightPx : 64; // fallback for unexpected CSS parsing
    const startSlot = Math.floor((workingHoursStartHour * 60) / CALENDAR_SLOT_MINUTES);
    calendarBodyRef.current.scrollTop = startSlot * rowHeight;
  }, [activeTab, workingHoursStartHour, showCalendar]);

  const taskViewLabel = useMemo(() => {
    if (isMobileBrowser) {
      if (mobileSection === 'unscheduled') return 'Unscheduled';
      if (mobileSection === 'scheduled') return 'Scheduled';
      return 'Calendar';
    }
    const activeKeys = Object.entries(webSections)
      .filter(([, on]) => on)
      .map(([key]) => key);
    if (activeKeys.length === 0 || activeKeys.length === 3) return 'All';
    if (activeKeys.length === 1) {
      const k = activeKeys[0];
      if (k === 'unscheduled') return 'Unscheduled';
      if (k === 'scheduled') return 'Scheduled';
      return 'Calendar';
    }
    return `${activeKeys.length} selected`;
  }, [isMobileBrowser, mobileSection, webSections]);

  const handleTaskViewToggle = (key) => {
    if (isMobileBrowser) {
      setMobileSection(key);
      setTaskViewDropdownOpen(false);
      return;
    }
    setWebSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const detailModalSaving =
    savingName ||
    savingDueDate ||
    savingAmount ||
    savingRepeat ||
    savingPriority ||
    savingBlockTimes ||
    savingDetail ||
    duplicatingDetail;

  return (
    <div className="page-container page-container--section-tabs">
      <SectionTabsPageHeader subtitle="Schedule tasks on your calendar and manage time blocks.">
        <TaskSectionTabs activeTab={TASK_TAB_TIME_BLOCKING} />
      </SectionTabsPageHeader>
      <div className="tb-page-header" style={{ justifyContent: "flex-end" }}>
        <div className="tb-page-header-actions">
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => setWorkingHoursModalOpen(true)}
            title="Start and end of your workday for auto-schedule and the daily calendar"
          >
            Working hours
          </button>
          {googleConfigured && (
            <button
              type="button"
              className={`btn btn-sm btn-primary-outline${googlePanelOpen ? ' active' : ''}`}
              onClick={() => setGooglePanelOpen((open) => !open)}
              aria-expanded={googlePanelOpen}
              aria-controls="tb-google-panel-body"
              title="Google Calendar"
            >
              <IconCalendar size={16} style={{ marginRight: 4 }} />
              Google Calendar {googlePanelOpen ? '▾' : '▸'}
            </button>
          )}
        </div>
      </div>
      {googleConfigured && googleAuthNotice && (
        <div
          role="alert"
          style={{
            marginBottom: 12,
            padding: '10px 12px',
            background: '#fff8e5',
            border: '1px solid #d4a72c',
            borderRadius: 6,
            fontSize: 14,
            color: '#24292f',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <span>{googleAuthNotice}</span>
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={() => setGoogleAuthNotice('')}
          >
            Dismiss
          </button>
        </div>
      )}
      {googleConfigured && googlePanelOpen && (
        <section
          className="tb-google-card card"
          style={{ marginBottom: 16, padding: '12px 16px' }}
          aria-labelledby="tb-google-heading"
        >
          <h3 id="tb-google-heading" style={{ margin: '0 0 8px', fontSize: 15 }}>
            Google Calendar
          </h3>
          <p style={{ margin: '0 0 12px', fontSize: 14, color: '#57606a' }}>
            Sync your time blocks with your own Google Calendar. Only your calendar is used. You grant access
            when you connect; you can disconnect anytime.
          </p>
          <div
            id="tb-google-panel-body"
            style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
          >
            {googleConnected ? (
              <>
                <span style={{ color: '#1a7f37', fontWeight: 500 }}>Your Google Calendar is connected</span>
                <span style={{ fontSize: 13, color: '#6e7681' }}>
                  Two-way sync: changes here sync to Google automatically. Changes in Google are pulled when you load this page or click Refresh from Google below. Blocks created before you connected can be pushed with Sync existing blocks.
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={handleRefreshFromGoogle}
                  disabled={googleLoading}
                >
                  {googleLoading ? 'Refreshing…' : 'Refresh from Google'}
                </button>
                {googleRefreshResult !== null && (
                  <span style={{ fontSize: 13, color: '#1a7f37' }}>
                    {(() => {
                      const r = typeof googleRefreshResult === 'object' ? googleRefreshResult : { updated: googleRefreshResult, movedToBacklog: 0 };
                      const u = r.updated ?? 0;
                      const m = r.movedToBacklog ?? 0;
                      if (u === 0 && m === 0) return 'Already in sync.';
                      const parts = [];
                      if (u > 0) parts.push(`Updated ${u} block(s) from Google.`);
                      if (m > 0) parts.push(`Moved ${m} to backlog (deleted in Google).`);
                      return parts.join(' ');
                    })()}
                  </span>
                )}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={handleSyncExistingToGoogle}
                  disabled={googleLoading}
                >
                  {googleLoading ? 'Syncing…' : 'Sync existing blocks to Google'}
                </button>
                {googleSyncResult !== null && (
                  <span style={{ fontSize: 13, color: '#1a7f37' }}>
                    {googleSyncResult === 0 ? 'No new blocks to sync.' : `Synced ${googleSyncResult} block(s) to Google Calendar.`}
                  </span>
                )}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleGoogleDisconnect}
                  disabled={googleLoading}
                >
                  {googleLoading ? 'Disconnecting…' : 'Disconnect'}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={handleGoogleConnect}
                  disabled={googleLoading}
                >
                  {googleLoading ? 'Redirecting to Google…' : 'Connect your Google Calendar'}
                </button>
                <span style={{ fontSize: 13, color: '#6e7681' }}>
                  You will be asked to sign in with Google and allow calendar access for your account only.
                </span>
              </>
            )}
          </div>
        </section>
      )}

      <div className="tb-top-row">
        <div className="tb-tabs" aria-label="View">
          <span style={{ fontSize: 12, color: '#57606a', marginRight: 8 }}>View:</span>
          <select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
            style={{
              padding: '4px 8px',
              fontSize: 13,
              borderRadius: 4,
              border: '1px solid #d0d7de',
            }}
            aria-label="View"
          >
            <option value="dailyPlanning">Daily</option>
            <option value="weeklyPlanning">Weekly</option>
          </select>
          <input
            type="text"
            value={textSearch}
            onChange={(e) => setTextSearch(e.target.value)}
            placeholder="Search name, project, category, notes…"
            className="input input-search input-search-wide"
            style={{ boxSizing: "border-box", marginLeft: 10, minWidth: 0, flex: "1 1 160px", maxWidth: 320 }}
            aria-label="Search tasks"
          />
        </div>

        <div className="tb-top-actions">
          {(activeTab === 'weeklyPlanning' || activeTab === 'dailyPlanning') && (
            <div className="tb-section-select">
              <span className="toolbar-label" style={{ marginRight: 4 }}>Task View:</span>
              <div ref={taskViewDropdownRef} className="dropdown-wrap tb-taskview-dropdown">
                <button
                  type="button"
                  onClick={() => setTaskViewDropdownOpen((open) => !open)}
                  className="dropdown-trigger"
                >
                  <span>{taskViewLabel}</span>
                  <span className="dropdown-arrow">{taskViewDropdownOpen ? '▲' : '▼'}</span>
                </button>
                {taskViewDropdownOpen && (
                  <div className="dropdown-panel w-180">
                    <label className="dropdown-option">
                      <input
                        type={isMobileBrowser ? 'radio' : 'checkbox'}
                        checked={selectedSections.unscheduled}
                        onChange={() => handleTaskViewToggle('unscheduled')}
                      />
                      <span>Unscheduled</span>
                    </label>
                    <label className="dropdown-option">
                      <input
                        type={isMobileBrowser ? 'radio' : 'checkbox'}
                        checked={selectedSections.calendar}
                        onChange={() => handleTaskViewToggle('calendar')}
                      />
                      <span>Calendar</span>
                    </label>
                    <label className="dropdown-option">
                      <input
                        type={isMobileBrowser ? 'radio' : 'checkbox'}
                        checked={selectedSections.scheduled}
                        onChange={() => handleTaskViewToggle('scheduled')}
                      />
                      <span>Scheduled</span>
                    </label>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="tb-tab-content">
        {(activeTab === 'weeklyPlanning' || activeTab === 'dailyPlanning') && (
          <div
            className={[
              'tb-weekly-layout',
              activeTab === 'dailyPlanning' ? 'tb-weekly-layout--daily' : '',
              (!showUnscheduledPanel && !showScheduledPanel) ? 'tb-weekly-layout--no-unscheduled' : '',
              (showScheduledPanel && !showUnscheduledPanel && showCalendar) ? 'tb-weekly-layout--calendar-scheduled' : '',
              (showUnscheduledPanel && showCalendar && showScheduledPanel) ? 'tb-weekly-layout--three' : '',
              (isMobileBrowser) ? 'tb-weekly-layout--single' : '',
              (activeTab === 'dailyPlanning' && showUnscheduledPanel && showCalendar && !showScheduledPanel) ? 'tb-weekly-layout--daily-two' : '',
            ].filter(Boolean).join(' ')}
          >
            {showUnscheduledPanel && (
              <div className={activeTab === 'dailyPlanning' ? 'tb-daily-unplanned' : undefined}>
                {renderUnscheduledPanel()}
              </div>
            )}

            {showCalendar && (
            <div className={activeTab === 'dailyPlanning' ? 'tb-calendar tb-calendar--daily' : 'tb-calendar'}>
              <div className="tb-calendar-header-row">
                <h3>{activeTab === 'weeklyPlanning' ? 'Weekly Calendar' : 'Daily Calendar'}</h3>
                <div className="tb-calendar-nav">
                  {activeTab === 'weeklyPlanning' ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={handlePrevWeek}
                        title="Previous week"
                        aria-label="Previous week"
                      >
                        <IconChevronLeft size={18} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={handleNextWeek}
                        title="Next week"
                        aria-label="Next week"
                      >
                        <IconChevronRight size={18} />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={handlePrevDay}
                        title="Previous day"
                        aria-label="Previous day"
                      >
                        <IconChevronLeft size={18} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={handleNextDay}
                        title="Next day"
                        aria-label="Next day"
                      >
                        <IconChevronRight size={18} />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <p className="tb-help">
                {activeTab === 'weeklyPlanning'
                  ? 'Drag tasks into a time slot to create a time block. Drag existing blocks to reschedule or resize to adjust duration.'
                  : 'Drag tasks into today or tomorrow to plan focused work. Drag existing blocks to reschedule or resize.'}
              </p>
              <div className={activeTab === 'weeklyPlanning' ? 'tb-calendar-weekly-scroll' : activeTab === 'dailyPlanning' ? 'tb-calendar-daily-scroll' : undefined}>
                <div className={`tb-calendar-grid${activeTab === 'weeklyPlanning' ? ' tb-calendar-grid--weekly' : ''}${activeTab === 'dailyPlanning' ? ' tb-calendar-grid--daily' : ''}`}>
                  <div className="tb-calendar-header">
                  <div />
                  {(activeTab === 'weeklyPlanning' ? days : dailyDays).map((d) => (
                    <div key={d.toISOString()}>
                      {d.toLocaleDateString(undefined, {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </div>
                  ))}
                </div>
                <div className="tb-calendar-body" ref={calendarBodyRef}>
                  {slots.map((slot, rowIdx) => (
                    <div key={rowIdx} className="tb-calendar-row">
                      <div className="tb-calendar-time">{formatSlotLabel(slot)}</div>
                      {(activeTab === 'weeklyPlanning' ? days : dailyDays).map((day, colIdx) => {
                        const cellStart = new Date(day.getTime());
                        setDateToSlotStart(cellStart, slot);
                        const cellEnd = new Date(cellStart.getTime() + CALENDAR_SLOT_MS);

                        const blocksForCell = timeBlocks.filter((b) => {
                          const start = new Date(b.start_time);
                          const end = new Date(b.end_time);
                          return (
                            start.getFullYear() === day.getFullYear()
                            && start.getMonth() === day.getMonth()
                            && start.getDate() === day.getDate()
                            && start < cellEnd
                            && end > cellStart
                          );
                        });
                        const googleEventsForCell = googleEvents.filter((ev) => {
                          if (syncedGoogleEventIds.has(ev.id)) return false;
                          const start = new Date(ev.start);
                          const end = new Date(ev.end);
                          return (
                            start.getFullYear() === day.getFullYear()
                            && start.getMonth() === day.getMonth()
                            && start.getDate() === day.getDate()
                            && start < cellEnd
                            && end > cellStart
                          );
                        });
                        const handleCellDoubleClick = (e) => {
                          if (e.target.closest('.tb-block')) return;
                          setSlotForNewTask({ day, slot });
                          setAddTaskOpen(true);
                        };

                        return (
                          <div
                            key={colIdx}
                            className="tb-calendar-cell"
                            onDragOver={(e) => { e.preventDefault(); }}
                            onDrop={(e) => handleDropOnCell(e, day, slot)}
                            onDoubleClick={handleCellDoubleClick}
                            title="Double-click to add a task in this slot"
                            aria-label={`Add task at ${day.toLocaleDateString(undefined, { weekday: 'short' })} ${formatSlotLabel(slot)}`}
                          >
                            {googleEventsForCell.map((ev) => {
                              const start = new Date(ev.start);
                              const end = new Date(ev.end);
                              const startSlot = getSlotIndexFromDate(start);
                              const endSlotExclusive = Math.ceil(
                                (end.getHours() * 60 + end.getMinutes()) / CALENDAR_SLOT_MINUTES
                              );
                              const lastRowSlot = endSlotExclusive - 1;
                              const isTop = slot === startSlot;
                              const isBottom = slot === lastRowSlot;
                              const isMiddle = !isTop && !isBottom;
                              const blockClasses = [
                                'tb-block',
                                'tb-block-google',
                                isTop ? 'tb-block--start' : '',
                                isMiddle ? 'tb-block--middle' : '',
                                isBottom ? 'tb-block--end' : '',
                              ].filter(Boolean).join(' ');
                              return (
                                <div key={ev.id} className={blockClasses} title={ev.summary}>
                                  {isTop && (
                                    <>
                                      <div className="tb-task-name">{ev.summary}</div>
                                      <div className="tb-task-meta tb-task-time-range">
                                        {start.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                                        {' – '}
                                        {end.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                                      </div>
                                    </>
                                  )}
                                </div>
                              );
                            })}
                            {blocksForCell.map((b) => {
                              const start = new Date(b.start_time);
                              const end = new Date(b.end_time);
                              const startSlot = getSlotIndexFromDate(start);
                              const endSlotExclusive = Math.ceil(
                                (end.getHours() * 60 + end.getMinutes()) / CALENDAR_SLOT_MINUTES
                              );
                              const lastRowSlot = endSlotExclusive - 1;
                              const isTop = slot === startSlot;
                              const isBottom = slot === lastRowSlot;
                              const isMiddle = !isTop && !isBottom;

                              const startLabel = start.toLocaleTimeString(undefined, {
                                hour: 'numeric',
                                minute: '2-digit',
                              });
                              const endLabel = end.toLocaleTimeString(undefined, {
                                hour: 'numeric',
                                minute: '2-digit',
                              });

                              const rawStatus = String(b.status || 'scheduled').toLowerCase();
                              const isCompleted = rawStatus === 'completed';
                              const isMissed =
                                rawStatus === 'missed'
                                || (!isCompleted && rawStatus === 'scheduled' && end < new Date());

                              const statusClass = isCompleted
                                ? 'tb-block--completed'
                                : isMissed
                                ? 'tb-block--missed'
                                : 'tb-block--scheduled';

                              const blockClasses = [
                                'tb-block',
                                statusClass,
                                isTop ? 'tb-block--start' : '',
                                isMiddle ? 'tb-block--middle' : '',
                                isBottom ? 'tb-block--end' : '',
                              ].filter(Boolean).join(' ');

                              return (
                                <div
                                  key={b._id}
                                  className={blockClasses}
                                  title={`${b.task_name || 'Time block'}\n${start.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} ${startLabel} – ${endLabel}`}
                                  draggable={isTop}
                                  onDragStart={(e) => {
                                    if (!isTop) return;
                                    e.dataTransfer.setData('type', 'block');
                                    e.dataTransfer.setData('blockId', b._id);
                                  }}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => setDetailTask({ source: 'block', ...b })}
                                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setDetailTask({ source: 'block', ...b }); }}
                                >
                                  {isTop && (
                                    <>
                                      <div className="tb-block-topline">
                                        <div className="tb-task-name">
                                          {b.task_name || 'Time block'}
                                        </div>
                                        <div className="tb-block-actions">
                                          <button
                                            type="button"
                                            className="tb-task-complete-btn btn-link-muted"
                                            style={iconBtnStyle}
                                            title="Mark Complete"
                                            disabled={completingBlockId === b._id}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleMarkBlockCompleted(b);
                                            }}
                                            aria-label="Close"
                                          >
                                            <IconClose size={18} />
                                          </button>
                                          <button
                                            type="button"
                                            className="tb-task-complete-btn btn-link-muted"
                                            style={iconBtnStyle}
                                            title="Move back to backlog"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleMoveMissedBlockToBacklog(b);
                                            }}
                                            aria-label="Move back to backlog"
                                          >
                                            <IconBacklog size={18} />
                                          </button>
                                        </div>
                                      </div>
                                      <div className="tb-task-meta tb-task-time-range">
                                        <span>
                                          {startLabel}
                                          {' – '}
                                          {endLabel}
                                        </span>
                                      </div>
                                    </>
                                  )}
                                  {isBottom && (
                                    <div
                                      className="tb-block-resize-hitarea"
                                      draggable
                                      onDragStart={(e) => {
                                        e.stopPropagation();
                                        e.dataTransfer.setData('type', 'block-resize');
                                        e.dataTransfer.setData('blockId', b._id);
                                        e.dataTransfer.setData('startDate', day.toISOString().slice(0, 10));
                                        e.dataTransfer.setData('startSlot', String(getSlotIndexFromDate(start)));
                                      }}
                                    />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
              </div>
            </div>
            )}

            {showScheduledPanel && renderScheduledPanel()}
          </div>
        )}
      </div>

      {detailTask && (
        <div
          className="tb-modal-backdrop"
          onClick={() => { void closeDetailTaskModal(); }}
          role="presentation"
        >
          <div
            className="tb-modal"
            onClick={(e) => e.stopPropagation()}
          >
            {(() => {
              const isBlock = detailTask.source === 'block';
              const blockStatusRaw = isBlock ? String(detailTask.status || 'scheduled').toLowerCase() : '';
              // Header status is based on the TASK being Open/Closed (not the block lifecycle).
              // If the time-block is completed, we treat the task as Closed; otherwise Open.
              const taskStatus = isBlock
                ? (blockStatusRaw === 'completed' ? 'Closed' : 'Open')
                : String(detailTask.status || 'Open');
              const canAutoScheduleInDetail =
                !isBlock
                && taskStatus === 'Open'
                && !taskHasTimeBlocks(detailTask);
              return (
                <div className="tb-modal-header">
                  <h3>
                    Task details
                    {taskStatus ? ` · Status: ${taskStatus}` : ''}
                  </h3>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {canAutoScheduleInDetail && (
                      <button
                        type="button"
                        className="btn-link btn-link-muted"
                        style={iconBtnStyle}
                        title="Auto schedule into earliest open 30-minute slot"
                        aria-label="Auto schedule"
                        disabled={detailModalSaving}
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleAutoSlotTask(detailTask).catch(() => {
                            setAutoSlotAlert({
                              open: true,
                              title: 'Auto schedule failed',
                              message: 'Could not search for an open slot. Try again.',
                            });
                          });
                        }}
                      >
                        <IconBacklog size={20} style={{ transform: 'scaleX(-1)' }} />
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn-link btn-link-muted"
                      style={iconBtnStyle}
                      title="Duplicate task"
                      aria-label="Duplicate task"
                      disabled={detailModalSaving}
                      onClick={() => { void handleDuplicateDetailTask(); }}
                    >
                      <IconDuplicate size={20} />
                    </button>
                    <button
                      type="button"
                      className="tb-modal-close"
                      onClick={() => { void closeDetailTaskModal(); }}
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>
                </div>
              );
            })()}
            <div className="tb-modal-body">
              <div className="cards-wrapper cards-wrapper--sm tb-modal-card-wrapper">
                {(() => {
                  const isBlock = detailTask.source === 'block';
                  const due = isBlock ? detailTask.task_due_date : detailTask.due_date;
                  const cardDate = due ? formatDueDateFull(due) : '';
                  const timePart = due ? getDueTimePart(due) : '';
                  const amount = !isBlock ? (detailTask.amount != null ? detailTask.amount : 0) : (detailTask.amount != null ? detailTask.amount : 0);
                  const repeatRuleDraft = detailRepeatDraft;
                  const status = (() => {
                    if (!isBlock) return detailTask.status || 'Open';
                    const s = String(detailTask.status || 'scheduled').toLowerCase();
                    if (s === 'missed') return 'Missed';
                    if (s === 'completed') return 'Completed';
                    return 'Scheduled';
                  })();
                  const statusClosed = status === 'Closed';
                  const priority = (detailPriorityDraft != null && String(detailPriorityDraft).trim() !== '') ? String(detailPriorityDraft).trim() : '';
                  const priorityClass = statusClosed
                    ? 'card--closed'
                    : (priority === 'High'
                      ? 'card--priority-high'
                      : priority === 'Low'
                        ? 'card--priority-low'
                        : 'card--priority-medium');

                  const blockTimeEditable = isBlock;
                  const timeDropdownOptions = taskAddDueTimeOptions.filter((opt) => opt.value);

                  return (
                    <div className={`card ${priorityClass}`}>
                      <div className="card-header">
                        <div
                          className="card-header-content"
                          style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}
                        >
                          {detailActiveField === 'name' ? (
                            <input
                              type="text"
                              value={detailNameDraft}
                              autoFocus
                              onChange={(e) => setDetailNameDraft(e.target.value)}
                              onKeyDown={fieldCommitOnEnterKeyDown}
                              onBlur={() => {
                                if (!detailInitialValues) {
                                  setDetailActiveField('');
                                  return;
                                }
                                const trimmed = String(detailNameDraft || '').trim();
                                if (trimmed === '') {
                                  setDetailNameDraft(detailInitialValues.name);
                                  setDetailActiveField('');
                                  return;
                                }
                                if (trimmed === String(detailInitialValues.name || '').trim()) {
                                  setDetailActiveField('');
                                  return;
                                }
                                void handleDetailNameCommit(detailNameDraft).then(() => setDetailActiveField(''));
                              }}
                              disabled={detailModalSaving}
                              style={{
                                width: '100%',
                                padding: '2px 6px',
                                fontSize: '14px',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px',
                                background: 'var(--tb-card-bg, #fff)',
                                color: 'inherit',
                                boxSizing: 'border-box',
                                fontWeight: 600,
                              }}
                              aria-label="Task name"
                            />
                          ) : (
                            <button
                              type="button"
                              className="btn-link"
                              style={{ textAlign: 'left', padding: 0, color: 'inherit', textDecoration: 'none', fontWeight: 600, fontSize: '14px' }}
                              onClick={() => setDetailActiveField('name')}
                              aria-label="Edit task name"
                            >
                              {detailNameDraft || '—'}
                            </button>
                          )}

                          {(detailDueDateDraft || cardDate || timePart) && (
                            <div className="card-date">
                              {detailActiveField === 'dueDate' ? (
                                <DateTimeFieldGroup
                                  layout="inline"
                                  date={detailDueDateDraft}
                                  onDateChange={setDetailDueDateDraft}
                                  time={detailDueTimeDraft}
                                  onTimeChange={setDetailDueTimeDraft}
                                  timeOptions={taskAddDueTimeOptions}
                                  onBlur={commitDetailDueFieldBlur}
                                  onKeyDown={fieldCommitOnEnterKeyDown}
                                  autoFocus
                                  disabled={detailModalSaving}
                                  dateAriaLabel="Due date"
                                  timeAriaLabel="Due time"
                                  dateStyle={{
                                    padding: '2px 6px',
                                    fontSize: '13px',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '4px',
                                    background: 'var(--tb-card-bg, #fff)',
                                    color: 'inherit',
                                    boxSizing: 'border-box',
                                  }}
                                  timeSelectStyle={{
                                    padding: '2px 6px',
                                    fontSize: '13px',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '4px',
                                    background: 'var(--tb-card-bg, #fff)',
                                    color: 'inherit',
                                    width: '92px',
                                  }}
                                  style={{ display: 'inline-flex', gap: '6px', alignItems: 'center', flexWrap: 'nowrap' }}
                                />
                              ) : (
                                <button
                                  type="button"
                                  className="btn-link"
                                  style={{ textAlign: 'left', padding: 0, color: 'inherit', textDecoration: 'none' }}
                                  onClick={() => setDetailActiveField('dueDate')}
                                  aria-label="Edit due date"
                                >
                                  {cardDate || '—'}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Intentionally no textual status in modal body for consistency */}
                      <div className="card-meta-muted tb-field-row">
                        <span className="tb-field-label">Amount($)</span>
                        {detailActiveField === 'amount' ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            value={detailAmountDraft}
                            autoFocus
                            onChange={(e) => setDetailAmountDraft(e.target.value)}
                            onKeyDown={fieldCommitOnEnterKeyDown}
                            onBlur={() => {
                              if (!detailInitialValues) {
                                setDetailActiveField('');
                                return;
                              }
                              if (String(detailAmountDraft || '') === String(detailInitialValues.amount || '')) {
                                setDetailActiveField('');
                                return;
                              }
                              void handleDetailAmountCommit(detailAmountDraft).then(() => setDetailActiveField(''));
                            }}
                            disabled={detailModalSaving}
                            placeholder={String(amount ?? 0)}
                            className="input input-number-no-spin"
                            style={{
                              padding: '2px 6px',
                              fontSize: '13px',
                              border: '1px solid #dee2e6',
                              borderRadius: '4px',
                              background: 'var(--tb-card-bg, #fff)',
                              color: 'inherit',
                              width: '110px',
                            }}
                            aria-label="Amount($)"
                          />
                        ) : (
                          <button type="button" className="btn-link" style={{ textAlign: 'left', padding: 0, color: 'inherit', textDecoration: 'none' }} onClick={() => setDetailActiveField('amount')}>
                            {String(detailAmountDraft || '0')}
                          </button>
                        )}
                      </div>
                      <div className="card-meta-muted tb-field-row">
                        <span className="tb-field-label">Repeat</span>
                        {detailActiveField === 'repeat' ? (
                          <RepeatPatternPicker
                            anchorDate={detailDueDateDraft}
                            value={repeatRuleDraft}
                            autoFocus
                            dropdownPortal
                            onChange={(rule) => {
                              setDetailRepeatDraft(rule);
                              if (detailInitialValues && JSON.stringify(rule) === JSON.stringify(detailInitialValues.repeat_rule)) {
                                setDetailActiveField('');
                                return;
                              }
                              void handleDetailRepeatChange(rule).then(() => setDetailActiveField(''));
                            }}
                            disabled={detailModalSaving}
                          />
                        ) : (
                          <button type="button" className="btn-link" style={{ textAlign: 'left', padding: 0, color: 'inherit', textDecoration: 'none' }} onClick={() => setDetailActiveField('repeat')}>
                            {getRepeatLabel(repeatRuleDraft)}
                          </button>
                        )}
                      </div>
                      <div className="card-meta-muted tb-field-row">
                        <span className="tb-field-label">Priority</span>
                        {detailActiveField === 'priority' ? (
                          <select
                            value={priority || ''}
                            autoFocus
                            onChange={(e) => {
                              const v = e.target.value;
                              setDetailPriorityDraft(v);
                              if (detailInitialValues && String(v || '') === String(detailInitialValues.priority || '')) {
                                setDetailActiveField('');
                                return;
                              }
                              void handleDetailPriorityChange(v).then(() => setDetailActiveField(''));
                            }}
                            onKeyDown={fieldCommitOnEnterKeyDown}
                            disabled={detailModalSaving}
                            style={{
                              padding: '2px 6px',
                              fontSize: '13px',
                              border: '1px solid #dee2e6',
                              borderRadius: '4px',
                              background: 'var(--tb-card-bg, #fff)',
                              color: 'inherit',
                              cursor: detailModalSaving ? 'wait' : 'pointer',
                              minWidth: '120px',
                            }}
                            aria-label="Priority"
                          >
                            <option value="">—</option>
                            <option value="Low">Low</option>
                            <option value="Medium">Medium</option>
                            <option value="High">High</option>
                          </select>
                        ) : (
                          <button type="button" className="btn-link" style={{ textAlign: 'left', padding: 0, color: 'inherit', textDecoration: 'none' }} onClick={() => setDetailActiveField('priority')}>
                            {priority || '—'}
                          </button>
                        )}
                      </div>
                      {(() => {
                        const projectLinkId = getDetailProjectIdForLink(detailTask);
                        const projectLabel = getDetailProjectDisplayName(detailTask);
                        if (!projectLinkId && !projectLabel) return null;
                        return (
                          <div className="card-meta-muted tb-field-row">
                            <span className="tb-field-label">Project</span>
                            {projectLinkId ? (
                              <Link
                                to={`/Task?projectId=${encodeURIComponent(projectLinkId)}`}
                                className="btn-link"
                                style={{
                                  textAlign: 'left',
                                  padding: 0,
                                  color: 'var(--app-primary, #2563eb)',
                                  textDecoration: 'none',
                                  fontWeight: 500,
                                  minWidth: 0,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  display: 'inline-block',
                                  maxWidth: '100%',
                                }}
                                title={projectLabel ? `Open tasks for ${projectLabel}` : 'Open tasks for this project'}
                              >
                                {projectLabel || 'View project'}
                              </Link>
                            ) : (
                              <span style={{ minWidth: 0 }}>{projectLabel}</span>
                            )}
                          </div>
                        );
                      })()}
                      {isBlock && (
                        <div className="card-meta-muted" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap', flexWrap: 'nowrap' }}>
                          Block:&nbsp;
                          {getDueDatePart(detailTask.start_time)}{' '}
                          {blockTimeEditable ? (
                            <>
                              {detailActiveField === 'blockStart' ? (
                                  <TimeSelect
                                    value={detailStartTimeDraft}
                                    options={timeDropdownOptions}
                                    onChange={(v) => {
                                      setDetailStartTimeDraft(v);
                                      void handleDetailBlockStartCommit(v);
                                    }}
                                    onBlur={() => {
                                      setDetailActiveField('');
                                    }}
                                    onKeyDown={fieldCommitOnEnterKeyDown}
                                    disabled={detailModalSaving}
                                    maxVisibleItems={5}
                                    dropdownPortal
                                    aria-label="Edit block start time"
                                  style={{
                                    padding: '2px 6px',
                                    fontSize: '13px',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '4px',
                                    background: 'var(--tb-card-bg, #fff)',
                                    color: 'inherit',
                                    width: '92px',
                                  }}
                                />
                              ) : (
                                <button
                                  type="button"
                                  className="btn-link"
                                  style={{ padding: 0, color: 'inherit', textDecoration: 'none', fontWeight: 600 }}
                                  onClick={() => setDetailActiveField('blockStart')}
                                  disabled={detailModalSaving}
                                  aria-label="Edit block start time"
                                >
                                  {new Date(detailTask.start_time).toLocaleTimeString(undefined, {
                                    hour: 'numeric',
                                    minute: '2-digit',
                                  })}
                                </button>
                              )}
                              {' – '}
                              {detailActiveField === 'blockEnd' ? (
                                <TimeSelect
                                  value={detailEndTimeDraft}
                                  options={timeDropdownOptions}
                                  onChange={(v) => {
                                    setDetailEndTimeDraft(v);
                                    void handleDetailBlockEndCommit(v);
                                  }}
                                  onBlur={() => {
                                    setDetailActiveField('');
                                  }}
                                  onKeyDown={fieldCommitOnEnterKeyDown}
                                  disabled={detailModalSaving}
                                  maxVisibleItems={5}
                                  dropdownPortal
                                  aria-label="Edit block end time"
                                  style={{
                                    padding: '2px 6px',
                                    fontSize: '13px',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '4px',
                                    background: 'var(--tb-card-bg, #fff)',
                                    color: 'inherit',
                                    width: '92px',
                                  }}
                                />
                              ) : (
                                <button
                                  type="button"
                                  className="btn-link"
                                  style={{ padding: 0, color: 'inherit', textDecoration: 'none', fontWeight: 600 }}
                                  onClick={() => setDetailActiveField('blockEnd')}
                                  disabled={detailModalSaving}
                                  aria-label="Edit block end time"
                                >
                                  {new Date(detailTask.end_time).toLocaleTimeString(undefined, {
                                    hour: 'numeric',
                                    minute: '2-digit',
                                  })}
                                </button>
                              )}
                            </>
                          ) : (
                            <>
                              {new Date(detailTask.start_time).toLocaleTimeString(undefined, {
                                hour: 'numeric',
                                minute: '2-digit',
                              })}
                              {' – '}
                              {new Date(detailTask.end_time).toLocaleTimeString(undefined, {
                                hour: 'numeric',
                                minute: '2-digit',
                              })}
                            </>
                          )}
                          {' · '}
                          {status}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      <TaskAddModal
        open={addTaskOpen}
        dueTimeOptions={taskAddDueTimeOptions}
        focusDueTime={workingHoursStart || undefined}
        title={slotForNewTask ? 'Add task (scheduled in this slot)' : 'Add task'}
        showAutoScheduleToggle={true}
        initialValues={slotForNewTask ? (() => {
          const d = new Date(slotForNewTask.day.getTime());
          setDateToSlotStart(d, slotForNewTask.slot);
          return { due_date: d.toISOString() };
        })() : undefined}
        onCancel={() => {
          setAddTaskOpen(false);
          setSlotForNewTask(null);
        }}
        onSave={handleCreateTaskFromModal}
      />
      <SmartAddModal
        open={smartAddOpen}
        accountId={accountId}
        createdBy={userId}
        othersProjectId={othersProjectId}
        onClose={() => setSmartAddOpen(false)}
        showAutoScheduleToggle={true}
        onCreated={async (autoSchedule, createdCount) => {
          const n = Number(createdCount) || 0;
          setAutoSlotAlert({
            open: true,
            title: 'Add Task (NLP)',
            message: `Done. ${n} task(s) created.`,
          });

          const beforeIds = smartAddUnscheduledBeforeRef.current || new Set();
          const refreshed = await refreshTasksAndBlocks();
          if (!autoSchedule || !refreshed || !Array.isArray(refreshed.tasks)) return;

          const newlyCreated = refreshed.tasks.filter((t) => {
            const id = t?._id != null ? String(t._id) : '';
            return id && !beforeIds.has(id);
          });

          // SmartAddModal may create one or more tasks; schedule each if possible.
          if (newlyCreated.length === 0) return;

          let lastSlotDay = null;
          for (let i = 0; i < newlyCreated.length; i++) {
            const taskToSchedule = newlyCreated[i];
            if (!taskToSchedule?._id) continue;

            const slotToUse = await findEarliestOpenSlotAhead();
            if (!slotToUse) {
              if (i === 0) {
                setAutoSlotAlert({
                  open: true,
                  title: 'No open slot found',
                  message: `No free 30-minute slot in the next ${Math.round(AUTO_SCHEDULE_SEARCH_DAYS / 7)} weeks during your working hours (and synced calendar, if any).`,
                });
              }
              break;
            }

            await createTimeBlock({
              account_id: accountId,
              task_id: taskToSchedule._id,
              user_id: userId,
              start_time: slotToUse.start.toISOString(),
              end_time: slotToUse.end.toISOString(),
            });
            lastSlotDay = slotToUse.start;
          }

          if (lastSlotDay) {
            await refreshTasksAndBlocksForSlotDay(lastSlotDay);
          }
        }}
      />

      <ConfirmationModal
        open={autoSlotDialog.open}
        title="Auto schedule task"
        mode="confirm"
        confirmLabel="Schedule"
        cancelLabel="Cancel"
        message={autoSlotDialog.task && autoSlotDialog.start && autoSlotDialog.end ? (() => {
          const startLabel = autoSlotDialog.start.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
          const endLabel = autoSlotDialog.end.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
          const dateLabel = autoSlotDialog.start.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });

          const [wsH = '0', wsM = '0'] = String(workingHoursStart || '').split(':');
          const [weH = '0', weM = '0'] = String(workingHoursEnd || '').split(':');

          const whStart = (() => {
            const d = new Date(autoSlotDialog.start.getTime());
            d.setHours(parseInt(wsH, 10) || 0, parseInt(wsM, 10) || 0, 0, 0);
            return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
          })();

          const whEnd = (() => {
            const d = new Date(autoSlotDialog.start.getTime());
            d.setHours(parseInt(weH, 10) || 0, parseInt(weM, 10) || 0, 0, 0);
            return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
          })();

          return (
            `Schedule "${autoSlotDialog.task.name}" at ${startLabel} – ${endLabel} on ${dateLabel}?` +
            `\n\nWorking hours: ${whStart} – ${whEnd}`
          );
        })() : 'Schedule this task in the earliest open 30-minute slot?'}
        onCancel={() => setAutoSlotDialog({ open: false, task: null, start: null, end: null })}
        onConfirm={async () => {
          if (!accountId || !userId || !autoSlotDialog.task?._id || !autoSlotDialog.start || !autoSlotDialog.end) return;

          await createTimeBlock({
            account_id: accountId,
            task_id: autoSlotDialog.task._id,
            user_id: userId,
            start_time: autoSlotDialog.start.toISOString(),
            end_time: autoSlotDialog.end.toISOString(),
            status: 'scheduled',
          });

          await refreshTasksAndBlocksForSlotDay(autoSlotDialog.start);

          setAutoSlotDialog({ open: false, task: null, start: null, end: null });
        }}
      />

      <ConfirmationModal
        open={autoSlotAlert.open}
        title={autoSlotAlert.title || 'Info'}
        mode="alert"
        message={autoSlotAlert.message || ''}
        onCancel={dismissAutoSlotAlert}
        onConfirm={dismissAutoSlotAlert}
      />

      <ConfirmationModal
        open={deleteUnscheduledTaskConfirm.open}
        title="Delete task"
        mode="confirm"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        message={deleteUnscheduledTaskConfirm.message || ''}
        onCancel={() => setDeleteUnscheduledTaskConfirm({ open: false, task: null, message: '' })}
        onConfirm={() => {
          void executeDeleteUnscheduledTask();
        }}
      />

      {workingHoursModalOpen ? (
        <div
          className="ProfileInfoModal-overlay"
          onClick={(e) => e.target === e.currentTarget && setWorkingHoursModalOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="tb-working-hours-title"
        >
          <div className="ProfileInfoModal-content" onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                marginBottom: 12,
                minWidth: 0,
              }}
            >
              <h3
                id="tb-working-hours-title"
                style={{ margin: 0, fontSize: '1.05rem', fontWeight: 600, color: '#111827', minWidth: 0 }}
              >
                Working hours
              </h3>
              <button
                type="button"
                onClick={() => setWorkingHoursModalOpen(false)}
                style={{
                  padding: 0,
                  margin: 0,
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: 'var(--app-primary)',
                  flexShrink: 0,
                }}
              >
                Close
              </button>
            </div>
            <ProfileWorkingHoursPanel fieldIdPrefix="tb-wh" />
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default TimeBlocking;

